/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import { ResponsiveTable } from "../../../components/mobile/ResponsiveTable";
import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "../../../components/ui/sonner";
import { Button } from "../../../components/ui/button";
import { useApp } from "../../../context/AppContext";
import { V3Header, Denied } from "./V3Header";
import { useV3Meta, WS_COLOR, WsPill, Progress, DsPill, DeadlineBadge, StdBaselineBadge, Ring, StaleChip, fmtD, fmtMD, formatProgress, discStatusLabel, isStageHiddenInProjects, isClosedProject, isHandedOff, isDesignCompleted, stageForecastDaysLeft, stageForecastState,
  STAGE_PHASE, STAGE_PHASE_LABEL, STAGE_PHASE_TONE, stagePhasesOf, stageConsolidationOf, canonicalStageKey } from "./meta";
import { ProjectDrawer } from "./ProjectDrawer";
/* 【2026-10-29 时区统一】矩阵「截止日取最晚」原来按字符串字典序取，库里 dueDate
   有纯日期 / 洛杉矶零点瞬间 / 历史建项时刻三种形态时会挑错日历日（IT 报的
   「12/21 vs 12/22」）。这里用 usDate 的 dateInputValue 归一到洛杉矶日历日再比。 */
import { dateInputValue } from "../../../lib/usDate";
/* 【2026-10-29】矩阵信息列的列宽 / 表头组件 / 列头口径说明统一放在 MatrixInfoTh.jsx：
   原先内联在本文件里，测试无法只挂载「一个表头格」来验 ⓘ（本文件顶层 import 了
   ESM-only 的 react-router-dom v7，jest 解析不了）。语义一字未改，仅搬家。 */
import { MatrixInfoTh, MATRIX_INFO_WIDTH, COL_TITLE } from "./MatrixInfoTh";
// 阶段简称表直接从详情阶段条复用，保证列表列名与详情截图（前期咨询 / 现场测量 / 方案设计 …）一致。
import { STAGE_SHORT, STAGE_SHORT_NOTE, CONCURRENT_GROUPS } from "./LegacyTabs";
import { HandedOffList } from "./HandedOffList";
// 「已结项」列表（2026-10-20）：不进工程部的项目，设计做完直接结束。
import { CompletedList } from "./CompletedList";
import { DeleteProjectDialog } from "./DeleteProjectDialog";
import { TypeReviewDialog } from "./TypeReviewDialog";
import { LayoutGrid, Table2, Kanban, Grid3X3, Search, Plus, MapPin, User, Loader2, Layers, ChevronLeft, ChevronRight, ArrowRightLeft, PauseCircle, HardHat, Trash2, Tag, UserX, Megaphone, XCircle, MessageSquareText, Timer, ArrowDownUp, Columns3, Check, SlidersHorizontal, ChevronDown, X, ClipboardCheck, Package, BadgeCheck, Eye, FileSignature, CalendarClock, AlertTriangle, Info } from "lucide-react";
import { SIGN_NUM, signElapsedText } from "./HeadStrip";
import { CancelledList } from "./CancelledList";
import { createPortal } from "react-dom";
import { NewProjectDialog } from "./NewProjectDialog";
import { DiscCfgDialog } from "./DiscCfgDialog";
import StageChatDrawer from "./StageChatDrawer";
const UnassignedChip = ({ p, tid }) => p.assign?.missing?.length ? <span data-testid={tid} data-overdue={p.assign.overdue ? 1 : 0} title={T("缺 {0} · 已 {1} 小时", [p.assign.missingLabels.map((x) => T(x)).join(" + "), Math.floor(p.assign.hours)])} className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${p.assign.overdue ? "bg-red-600 text-white" : "bg-red-100 text-red-700"}`}><UserX className="h-3 w-3" />{T("未分配")}</span> : null;

/** 新 Comment 角标（反馈 FB973a773a3a）：项目看板 / 卡片看板显示未读评论条数，
 *  点开「评论」页签即记已读 → 关闭抽屉后角标自动消失（后端 design_v3_comment_reads 记时间戳）。 */
const CommentBadge = ({ n, tid, onClick, label }) => !n ? null : (
  <span role="button" tabIndex={-1} data-testid={tid} data-count={n}
    onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
    title={label || T("{0} 条新评论", [n])}
    className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200 hover:bg-sky-200 cursor-pointer">
    <MessageSquareText className="h-3 w-3" />{n}
  </span>
);

import { T, pickL } from "../../../lib/i18n";
import { ThFilter } from "./ThFilter";
import { PresignStatsCard } from "./PresignStatsCard";
// 签约前营销方案只读记录（2026-10-20）：已签约项目的历史留痕，点开看「当初谁做的」。
import { PresignRecordDialog } from "./PresignRecordDialog";
import { DS_COLOR } from "./meta";
import { ink } from "../../../lib/ink";
import { CardSkeleton, TableSkeleton } from "../../../components/States";
import { errTextOf } from "../../../lib/detailText";
import DesignContractHandoff from "./DesignContractHandoff";

const BR = [["all", "全部分公司"], ["los_angeles", "洛杉矶"], ["norcal", "北加湾区"], ["san_diego", "圣地亚哥"]];
const PROG_RANGES = [["0-24", "0–24%", (v) => v < 25], ["25-49", "25–49%", (v) => v >= 25 && v < 50], ["50-74", "50–74%", (v) => v >= 50 && v < 75], ["75-99", "75–99%", (v) => v >= 75 && v < 100], ["100", "100%", (v) => v >= 100]];
const SORTS = [["ws_desc", "工作状态 结束 → 开始"], ["owner_us", "美国负责人 A → Z"], ["owner_cn", "中国负责人 A → Z"], ["created", "最新创建"], ["address", "地址 A → Z"], ["updated", "最近更新"], ["progress_desc", "进度 高 → 低"], ["progress_asc", "进度 低 → 高"], ["deadline", "阶段预计日期 近 → 远"], ["sign_desc", "设计阶段耗时 长 → 短"], ["sign_asc", "设计阶段耗时 短 → 长"]];
const SIGN_LV = [["green", "≤3 个月", "#059669"], ["orange", "4–6 个月", "#ea580c"], ["red", ">6 个月", "#dc2626"], ["paused", "暂停中", "#d97706"]];
const inSign = (p, k) => (k === "paused" ? !!p.signInfo?.paused : p.signInfo?.level === k);
const DEFAULT_SORT_LABEL = "默认：工作状态 开始 → 结束";
const inProg = (v, k) => v != null && (PROG_RANGES.find(([kk]) => kk === k)?.[2] || (() => true))(v);
const ownerKey = (p) => p.owner || "未指派";
const cnKey = (p) => p.cdDesigner || "未指派";
const LAST = "\uffff";
const sortNames = (arr) => arr.sort((a, b) => (a === T("未指派")) - (b === T("未指派")) || a.localeCompare(b, "zh"));

/** 单个项目是否通过当前筛选；skip = 忽略某一列自己的筛选（用于表头选项计数） */
const passes = (p, f, skip = "") => {
  if (skip !== "branch" && f.branch !== "all" && p.branch !== f.branch) return false;
  if (skip !== "status" && f.status !== "all" && p.workStatus !== f.status) return false;
  if (skip !== "status" && f.group !== "all" && p.ws?.group !== f.group) return false;
  if (skip !== "owner" && f.owner !== "all" && ownerKey(p) !== f.owner && (p.owner || T("未指派")) !== f.owner) return false;
  if (skip !== "owner" && f.unassigned === "1" && !p.assign?.missing?.length) return false;
  if (skip !== "owner" && f.cd && (f.cd === "__none" ? !!p.cdDesigner : (p.cdDesigner || "") !== f.cd)) return false;
  /* 逾期筛选（2026-10-29 扩口径，修用户报的「详情 N 项逾期却不进名单」）：
     原先只认 `legacyOverdue.count > 0` —— 那是**任务级**口径，只统计 design_tasks
     一张表，阶段本身、交付物、机构、结构清单都不算。用户实测 414 Dayton 详情 13 项、
     169 Pinehaven 详情 9 项，任务行却一条都不逾期，于是 count=0、项目进不了名单，
     看上去像「筛选和详情打架」。

     现在改成「任务级 或 节点检查级，任一有逾期就入选」：
       · legacyOverdue.count  → 任务级（与每日「阶段任务逾期晨报」同一口径，保持不变）
       · nodeOverdue.overdue  → 与详情 Node check 同源的布尔值（后端本次新增）
     两者是并集而不是替换，所以晨报数字不动、列表也不会再漏项目；
     卡片上的角标按来源分别显示（见下方 legacy / node 两个 chip），
     用户能看出这条是「任务逾期」还是「节点逾期」，不至于以为是同一个数。
     旧 URL 参数（legacy=overdue）语义保持不变，只是命中范围变宽。 */
  const legacyHit = (p.legacyOverdue?.count || 0) > 0;
  const nodeHit = !!p.nodeOverdue?.overdue;
  if (skip !== "legacy" && f.legacy === "overdue" && !(legacyHit || nodeHit)) return false;
  if (f.phase !== "all" && p.phase !== f.phase) return false;
  const forecastState = stageForecastState(p);
  if (skip !== "deadline" && f.deadline !== "all" && !(f.deadline === "attention" ? ["overdue", "due_soon"].includes(forecastState) : forecastState === f.deadline)) return false;
  /* 承诺日口径（2026-10-27 修用户报的「蓝卡有数字、点进去 0 个项目」）：
     总览蓝卡「节点逾期 / 临期」原来的数字来自后端 `deadlineState`（按项目承诺日 targetDate 算），
     但点进来这个页面后，同一个 `deadline=attention` 参数却被上面这段代码按**阶段预计**
     （stageForecastDate，即所有阶段里最晚的那个 dueDate）重新过滤了一遍 ——
     两个字段完全不是一回事，于是卡片显示 5/3、列表 0 条。

     正式库实测（2026-10-27）差距有多大：活跃项目 68 个里，承诺日逾期 23、临期 7；
     而按阶段预计（=阶段链最晚 dueDate）逾期 0、临期 0 —— 阶段预计永远指向排期末端，
     现实中几乎不可能过期。所以**不能**反过来让卡片改读阶段预计，否则卡片会永远显示 0/0。

     结论：两个概念各自保留，各用各的参数，互不冒充：
       · `deadline=` → 阶段预计（列表「阶段预计」列与下拉，选项文案已明确写「阶段预计…」）；
       · `target=`   → 承诺日（总览蓝卡下钻专用，后端 KPI 与它同源）。
     这里读的 `p.deadlineState` 由后端 `_decorate` 按 targetDate 计算，
     与总览 KPI 的 `deadlineState` 是同一个字段，数字必然对得上。 */
  if (skip !== "target" && f.target !== "all" && !(f.target === "attention" ? ["overdue", "due_soon"].includes(p.deadlineState) : p.deadlineState === f.target)) return false;
  if (skip !== "deadline" && f.stale === "1" && !p.isStale) return false;
  if (skip !== "prog" && f.prog && !inProg(p.progress, f.prog)) return false;
  if (skip !== "sign" && f.signLv && !inSign(p, f.signLv)) return false;
  if (skip !== "dsf" && f.dsf && !(p.disciplines || []).some((x) => x.status === f.dsf)) return false;
  if (f.q) {
    const q = f.q.toLowerCase();
    const managerHit = (p.effectiveManagers || []).some((m) => `${m.name || ""} ${m.username || ""}`.toLowerCase().includes(q));
    if (![...[p.address, p.city, p.ownerName, p.projectId, p.owner], ...(p.effectiveManagers || []).map((m) => m.name)].some((x) => (x || "").toLowerCase().includes(q)) && !managerHit) return false;
  }
  return true;
};
const sortList = (ps, f, wsOrder = {}) => {
  const by = (fn) => [...ps].sort(fn);
  const wsi = (p) => wsOrder[p.workStatus] ?? 99;
  const byWs = (dir) => by((a, b) => dir * (wsi(a) - wsi(b)) || dir * ((a.progress || 0) - (b.progress || 0)) || (b.createdAt || "").localeCompare(a.createdAt || ""));
  if (f.sort === "ws_desc") return byWs(-1);
  if (f.sort === "owner_us") return by((a, b) => (a.owner || LAST).localeCompare(b.owner || LAST) || wsi(a) - wsi(b));
  if (f.sort === "owner_cn") return by((a, b) => (a.cdDesigner || LAST).localeCompare(b.cdDesigner || LAST) || wsi(a) - wsi(b));
  if (f.sort === "created") return by((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
  if (f.sort === "address") return by((a, b) => (a.address || "").localeCompare(b.address || ""));
  if (f.sort === "updated") return by((a, b) => (b.lastUpdatedAt || "").localeCompare(a.lastUpdatedAt || ""));
  if (f.sort === "progress_desc") return by((a, b) => (a.progress == null) - (b.progress == null) || (b.progress ?? 0) - (a.progress ?? 0));
  if (f.sort === "progress_asc") return by((a, b) => (a.progress == null) - (b.progress == null) || (a.progress ?? 0) - (b.progress ?? 0));
  if (f.sort === "deadline") return by((a, b) => (stageForecastDaysLeft(a) ?? 9999) - (stageForecastDaysLeft(b) ?? 9999));
  if (f.sort === "sign_desc") return by((a, b) => (b.signInfo?.days ?? -1) - (a.signInfo?.days ?? -1));
  if (f.sort === "sign_asc") return by((a, b) => (a.signInfo?.days ?? 99999) - (b.signInfo?.days ?? 99999));
  /* 逾期排序（2026-10-29 扩口径）：与筛选同样取两套口径的较早者，
     否则「按逾期排」时任务级为 0、节点级逾期 30 天的项目会掉到队尾，
     和刚扩宽的名单顺序自相矛盾。任务数相同再比节点最早逾期天数。 */
  if (f.legacy === "overdue") return by((a, b) => ((b.nodeOverdue?.oldestDays || 0) - (a.nodeOverdue?.oldestDays || 0))
    || ((b.legacyOverdue?.count || 0) - (a.legacyOverdue?.count || 0)));
  if (f.stale === "1") return by((a, b) => (b.staleDays || 0) - (a.staleDays || 0));
  if (f.unassigned === "1") return by((a, b) => (b.assign?.hours || 0) - (a.assign?.hours || 0));
  return byWs(1);
};

export default function DesignV3Projects({ mode = "active" }) {
  const presign = mode === "presign";
  const base = presign ? "/design/presign" : "/design/projects";
  const { api, user } = useApp();
  const nav = useNavigate();
  const { pid } = useParams();
  const [sp, setSp] = useSearchParams();
  const meta = useV3Meta();
  const [d, setD] = useState(null);
  // 手机首次进入使用已有项目卡片，避免默认十几列矩阵；手机 / 桌面各自记忆选择，
  // 经理在电脑看矩阵不会把手机偏好覆盖，仍可主动切回任意视图和原有筛选。
  const [viewStorageKey] = useState(() => window.matchMedia?.("(max-width: 767px)").matches ? "dv3_view_mobile_v1" : "dv3_view_v2");
  const [view, setView] = useState(() => localStorage.getItem(viewStorageKey) || (viewStorageKey === "dv3_view_mobile_v1" ? "cards" : "matrix"));
  // 手机不让两组筛选占满首屏：折叠只改变展示，保留已有查询条件并用数量提示；桌面始终展开。
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  /* 手机从总览蓝卡下钻（?target=attention）时自动展开筛选面板一次（2026-10-27）。
     为什么需要：窄屏下整个筛选区是 `hidden` + 折叠，若 URL 带着一条用户没见过的
     承诺日筛选进来，他只会看到「项目比平时少」而找不到任何筛选痕迹、也找不到清除入口。
     这里只在首帧按初始 URL 判一次，不监听后续变化 —— 用户自己收起面板后
     不能再被自动弹开，否则等于抢控制权。 */
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get("target")) setMobileFiltersOpen(true);
    } catch (e) { /* 极端环境下 location 不可用，忽略 */ }
  }, []);
  /* 表格 / 矩阵共用的信息列可见性偏好（2026-10-18 扩展矩阵）。
     只覆盖「项目」之后的信息列；项目与阶段列固定显示，
     这样同一份偏好切视图后仍然成立，不会出现表格隐藏了负责人、矩阵又冒出来。

     2026-10-20（用户需求「这个可以保存，不要每次进来都设置一下」）：
     原实现只写浏览器 localStorage（dv3_cols_v1），是**浏览器级**的 —— 换电脑 / 换浏览器 /
     清缓存后列设置就丢了，用户每进一个新环境都要重设一遍。
     现在改成**账号级**持久化：真正的值存后端 users/me/prefs.dv3Cols，本机 localStorage
     只当「首屏缓存」，登录后用接口返回值覆盖。

     为什么还保留 localStorage：
     1) 首屏不必等接口就能按上次的列渲染，避免表格列先全出来再「跳」成隐藏态；
     2) 接口失败（离线 / 后端异常）时仍能用本机缓存兜底，不至于直接回退成全部显示。
     本机缓存 key 带用户名（dv3_cols_v1:<username>），同一台电脑换账号登录不会串用别人的列。 */
  const colsLocalKey = `${COL_PREF_KEY}:${user?.username || "anon"}`;
  const readLocalCols = () => {
    try {
      // 兼容老版本：旧 key（不带用户名）是浏览器级设置，首次升级时直接沿用它，
      // 免得用户升级后突然发现列恢复成全部显示、又要重设一次。
      const raw = JSON.parse(localStorage.getItem(colsLocalKey) ?? localStorage.getItem(COL_PREF_KEY) ?? "[]");
      return Array.isArray(raw) ? raw.filter((k) => CONFIG_COLS.some(([kk]) => kk === k)) : [];
    } catch (e) { return []; } // 隐私模式 / 脏数据：退回「全部显示」
  };
  const [hiddenCols, setHiddenCols] = useState(readLocalCols);
  // 是否已经从账号偏好取回列设置。取回前不允许把本机缓存回写账号：
  // 否则「新设备首屏缓存是空」会把账号里已有的隐藏列覆盖成全部显示，正是要修的问题。
  const colsSynced = useRef(false);
  // 用户是否在本轮已经手动改过列。接口比人慢是常事，
  // 如果用户抢先点了勾选、随后接口才返回，直接套用服务端值会把用户刚做的操作吞掉，
  // 所以一旦用户动过就以本地为准（同时下面的 effect 会把新值写回账号）。
  const colsTouched = useRef(false);
  useEffect(() => {
    let live = true;
    api.get("/users/me/prefs").then(({ data }) => {
      if (!live || colsTouched.current) { colsSynced.current = true; return; }
      const server = data?.dv3Cols;
      if (Array.isArray(server)) {
        // 账号已有设置 → 以账号为准（换设备登录即恢复）
        setHiddenCols(server.filter((k) => CONFIG_COLS.some(([kk]) => kk === k)));
      } else if (hiddenCols.length) {
        // 账号还没存过（老账号升级）→ 把本机现有设置提升为账号设置，避免升级后要重设
        api.put("/users/me/prefs", { dv3Cols: hiddenCols }).catch(() => {});
      }
      colsSynced.current = true;
    }).catch(() => { colsSynced.current = true; }); // 接口失败：沿用本机缓存，不阻断页面
    return () => { live = false; };
  }, [api]); // eslint-disable-line
  useEffect(() => {
    try {
      localStorage.setItem(colsLocalKey, JSON.stringify(hiddenCols));
      // 老版本浏览器级 key 已迁到「按用户名」的新 key，升完就删，避免同机换账号时串用别人的列。
      if (localStorage.getItem(COL_PREF_KEY) != null) localStorage.removeItem(COL_PREF_KEY);
    } catch (e) { /* 隐私模式忽略 */ }
    // 取回账号值之前只写本机缓存，不回写账号（见 colsSynced 注释）
    if (colsSynced.current) api.put("/users/me/prefs", { dv3Cols: hiddenCols }).catch(() => { /* 保存失败不打断操作，下次改动会再存 */ });
  }, [hiddenCols, colsLocalKey, api]);
  // 列勾选 / 恢复全部显示都走这里，统一打「用户已手动调整」标记
  const setColsByUser = (fn) => { colsTouched.current = true; setHiddenCols(fn); };
  const [creating, setCreating] = useState(false);
  const [typeReview, setTypeReview] = useState(false);
  const f = { branch: sp.get("branch") || "all", status: sp.get("status") || "all", owner: sp.get("owner") || "all", phase: sp.get("phase") || "all", q: sp.get("q") || "", group: sp.get("group") || "all", deadline: sp.get("deadline") || "all", target: sp.get("target") || "all", legacy: sp.get("legacy") || "all", stale: sp.get("stale") || "", list: sp.get("list") || "active", receipt: sp.get("receipt") || "", cd: sp.get("cd") || "", unassigned: sp.get("unassigned") || "", prog: sp.get("prog") || "", dsf: sp.get("dsf") || "", sort: sp.get("sort") === "risk" ? "" : sp.get("sort") || "", signLv: sp.get("signLv") || "" };
  const setF = (k, v) => { const n = new URLSearchParams(sp); if (!v || v === "all" || v === "") n.delete(k); else n.set(k, v); setSp(n, { replace: true }); };
  const setMany = (obj) => { const n = new URLSearchParams(sp); Object.entries(obj).forEach(([k, v]) => { if (!v || v === "all" || v === "") n.delete(k); else n.set(k, v); }); setSp(n, { replace: true }); };
  // 停用旧风险评分后，清理收藏链接中的旧筛选 / 排序，保留其余查询条件。
  useEffect(() => {
    if (!sp.has("risk") && sp.get("sort") !== "risk") return;
    const n = new URLSearchParams(sp);
    n.delete("risk");
    if (n.get("sort") === "risk") n.delete("sort");
    setSp(n, { replace: true });
  }, [sp, setSp]);
  const handed = !presign && f.list === "handed";
  // 「已结项」列表（2026-10-20 用户需求：不需要移交施工的项目，设计做完就直接结束）。
  // 与 handed 并列但独立：结项项目没有工程项目 / PM / 移交回执，不能混进移交列表。
  const completedTab = !presign && f.list === "completed";
  const pausedTab = !presign && f.list === "paused";
  const cancelledTab = !presign && f.list === "cancelled";
  // 已签合同转设计由设计部内部操作人处理，不让客户在上传端选择新旧项目。
  const canHandoffContracts = ["admin", "ceo", "design_manager", "cn_architect", "us_policy"].includes(meta?.me?.role);
  const contractTab = !presign && f.list === "contracts" && canHandoffContracts;
  const listOnly = handed || cancelledTab || completedTab || contractTab;
  // 终态判定统一走 meta.js 的 isClosedProject / isHandedOff：
  // 新增 design_completed 后，原先只认 handed_off 的地方会漏掉结项项目，
  // 让它们在「进行中」里继续显示，甚至在暂停 / 签约前列表里被重复计算。
  const isHanded = (p) => isHandedOff(p);
  const isDone = (p) => isDesignCompleted(p);
  const isPre = (p) => p.phase === "pre_sign" && !isClosedProject(p);
  const isPaused = (p) => p.workStatus === "on_hold" && !isClosedProject(p) && !isPre(p);
  /* 签约前营销方案分类（2026-10-20 用户需求）。
     需求原话：「哪怕已签约了，也要保留是这个人做的签约前的营销方案这个字段或者记录，
     也就是说展示还是应该展示出来的，只是在签约前营销方案中增加分类的筛选，展示对应的列表」。
     改造前 `isPre` 只认 phase === "pre_sign"，项目一签约就整条从这个菜单消失，
     事后查不到「方案当初是谁做的」。
     现在改为读后端 `presignInfo.category`（与详情页同一份 _presign_view 判定）：
       · open   = 签约前、方案还没交 → 未完成
       · done   = 签约前、方案已交客户 → 已完成（还在等签约）
       · signed = 已签约，且存在明确的签约前方案证据 → 已签约（只读留痕）
     `presignInfo` 为 null 的历史导入项目（建项即签约、从未做过签约前方案）不进本菜单，
     否则正式库 78 个签约项目会全被算成「做过方案」。
     已签约档只做展示：阶段 / 任务 / 负责人记录一律不改，与「记录保留、只调显示」的口径一致。 */
  const preCat = (p) => (p.presignInfo ? p.presignInfo.category : null);
  const isPresignRow = (p) => ["open", "done", "signed"].includes(preCat(p));
  const [deleting, setDeleting] = useState(null);
  const canDelete = ["admin", "ceo"].includes(meta?.me?.role);
  // 2026-10-19（用户需求）：旧的「产物会话抽屉」整体下线，产物只做展示。
  // 矩阵格子改为阶段节点：点击阶段打开阶段会话，右侧展示该阶段挂载的产物，
  // 会话内容走项目评论的同一套 stageKey 数据，详情页可以直接看到并追溯。
  const [chatStage, setChatStage] = useState(null);
  const [discCfg, setDiscCfg] = useState(false);
  // 签约前营销方案只读记录弹窗（2026-10-20）：已签约留痕行点开看「当初谁做的」。
  const [recordPid, setRecordPid] = useState(null);
  // 详情抽屉里「会话产物」只读区需要跟随会话变化刷新：会话抽屉关闭时自增，ProjectDrawer 透传给 Disciplines 触发重拉
  const [detailRefresh, setDetailRefresh] = useState(0);
  const load = () => api.get("/design/v3/projects", presign ? { params: { presign: 1 } } : undefined).then(({ data }) => setD(data)).catch((e) => { setD({ projects: [], error: e?.detailText || e.message }); });
  useEffect(() => { load(); }, [api, presign]); // eslint-disable-line
  // 看板 / 卡片看板的新 Comment 角标（反馈 FB973a773a3a）：后端按「本人已读时间戳」算未读条数，
  // 抽屉里打开「评论」页签即刷新时间戳 → 关闭抽屉重拉本接口，角标自动消失。
  const [cUnread, setCUnread] = useState({});
  const loadUnread = () => api.get("/design/v3/comment-unread").then(({ data }) => setCUnread(data.counts || {})).catch(() => {});
  useEffect(() => { loadUnread(); }, [api]); // eslint-disable-line
  // 2026-10-15 视图偏好 key 升到 dv3_view_v2 —— 需求「强制所有用户默认落矩阵」：
  // 换 key 等于把历史偏好（老用户存的是 cards / table）一次性作废，所有人下次进来都走 fallback = matrix；
  // 之后用户自己切视图仍会被正常记住。旧 key 顺手清掉，免得以后排查时误读成「当前生效值」。
  useEffect(() => { try { localStorage.removeItem("dv3_view"); } catch (e) { /* 隐私模式下 localStorage 可能不可用，忽略 */ } }, []);
  useEffect(() => { localStorage.setItem(viewStorageKey, view); }, [view, viewStorageKey]);
  useEffect(() => { localStorage.setItem("dv3_branch", f.branch); }, [f.branch]);
  /* 签约前营销方案菜单的行集（2026-10-20 用户需求）：
     不再只认「当前处于签约前」，而是三档都收 —— 未完成 / 已完成 / 已签约。
     已签约项目以 presignInfo.evidence 为准判定「确实做过签约前方案」，
     历史导入（建项即签约）的项目 presignInfo 为 null，不会混进来。
     分类筛选走 URL 参数 pcat，默认 all（三档全看）。 */
  const preCatTab = presign ? (sp.get("pcat") || "all") : "all";
  const all = useMemo(() => {
    if (!presign) return (d?.projects || []).filter((p) => (pausedTab ? isPaused(p) : !isClosedProject(p) && !isPre(p) && !isPaused(p)));
    return (d?.projects || []).filter((p) => isPresignRow(p) && (preCatTab === "all" || preCat(p) === preCatTab));
  }, [d, presign, pausedTab, preCatTab]); // eslint-disable-line
  const wsOrder = useMemo(() => Object.fromEntries((meta?.workStatuses || []).map((s, i) => [s.key, i])), [meta]);
  /* 【2026-11-04 用户反馈：列表搜索输入特别卡】
     根因：默认视图是「矩阵」，一次渲染 130 行 × 18 列、约 858 个格子 / 19000 个 DOM 节点，
     每个格子都在现场算标题串（T()）、合并格的 aggregateStageCell、逾期三态与颜色。
     搜索框是受控输入，每敲一个字都用新 q 重新过滤 + 重排 + **把整个矩阵同步重渲染一遍**，
     实测每键阻塞 245~1000ms，于是按键主线程被占满、输入肉眼可见地卡
     （接口本身不慢：130 个项目一次 0.6s，且加载只在进页面时发生）。

     修法是两层，缺一层都不够：
       ① 防抖 250ms（用户 2026-11-04 追问「要不要加防抖」后确认）：连续打字期间**一次都不渲染**，
          停下来 250ms 后才过滤一次。只用 deferred 的话，每敲一个字仍然会排队一次完整渲染，
          打字快时一直在烧 CPU，虽然不挡输入但机器热的、列表也一直在闪。
       ② useDeferredValue：防抖到点后仍把它压在低优先级，保证这一帧先响应输入与其它交互。
     清空搜索立即生效（不等防抖），否则点 X 之后要愣 250ms 才看到全量列表。 */
  const [qSettled, setQSettled] = useState(f.q);
  useEffect(() => {
    if (!f.q) { setQSettled(""); return undefined; }
    if (f.q === qSettled) return undefined;
    const t = setTimeout(() => setQSettled(f.q), 250);
    return () => clearTimeout(t);
  }, [f.q, qSettled]);
  const deferredQ = useDeferredValue(qSettled);
  const qStale = qSettled !== f.q || deferredQ !== qSettled;
  const effF = useMemo(() => (deferredQ === f.q ? f : { ...f, q: deferredQ }), [f, deferredQ]);
  const list = useMemo(() => sortList(all.filter((p) => passes(p, effF)), effF, wsOrder), [all, wsOrder, effF]);
  /* 【2026-11-04 用户要求「做滚动加载吧」】
     背景：列表 / 矩阵一次渲染全部项目（130 个项目 = 858 个矩阵格 / 19000 个 DOM 节点），
     滚动和搜索都跟着变慢。这里先做**前端分块渲染**：首屏 30 行，滚到接近底部再追加 30 行。
     为什么不做服务端分页：筛选（passes）目前在前端对所有项目做，若服务端只给一页，
     「按负责人筛」这类条件就搜不到不在本页的项目；要服务端分页得先把筛选搬到后端，
     那是另一件事。分块渲染不动数据口径，只控制 DOM 数量，风险最小。
     展示条数在筛选条件或视图变化时重置回 30，避免换了筛选却停在「已显示 120 条」。 */
  const [shownN, setShownN] = useState(30);
  useEffect(() => { setShownN(30); }, [list, view]);
  const shownList = useMemo(() => list.slice(0, shownN), [list, shownN]);
  const moreRef = useRef(null);
  useEffect(() => {
    if (shownN >= list.length) return undefined;
    const el = moreRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    /* rootMargin 400px：快滚到底前提早加载，用户看不到「空白等待」。
       root 用默认的视口 —— 表格 / 矩阵的滚动容器在视口内，sentinel 被容器裁剪，
       滚到容器底部时才会与视口相交，正是想要的时机。 */
    const io = new IntersectionObserver((ents) => {
      if (ents.some((e) => e.isIntersecting)) setShownN((n) => Math.min(n + 30, list.length));
    }, { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [shownN, list.length]);
  const listFooter = list.length > shownList.length ? (
    <div ref={moreRef} className="py-3 text-center text-[11px] text-slate-400" data-testid="dv3-load-more"
      data-shown={shownList.length} data-total={list.length}>
      {T("已显示 {0} / {1}", [shownList.length, list.length])}
      <button type="button" onClick={() => setShownN((n) => Math.min(n + 30, list.length))}
        className="ml-2 font-semibold text-violet-700 hover:underline" data-testid="dv3-load-more-btn">{T("加载更多")}</button>
    </div>
  ) : null;

  const owners = useMemo(() => sortNames(Array.from(new Set(all.map((p) => p.owner || T("未指派"))))), [all]);
  const cnOwners = useMemo(() => sortNames(Array.from(new Set(all.map((p) => p.cdDesigner || T("未指派"))))), [all]);
  const handedN = (d?.projects || []).filter(isHanded).length;
  const doneN = (d?.projects || []).filter(isDone).length;
  const preN = (d?.projects || []).filter(isPre).length;
  const pausedN = (d?.projects || []).filter(isPaused).length;
  /* 三档计数（2026-10-20）：直接读 presignInfo.category，与后端分类同源，
     避免「下拉写 7、列表里 5」这种两个数字对不上的情况。 */
  const preOpenN = (d?.projects || []).filter((p) => preCat(p) === "open").length;
  const preDoneN = (d?.projects || []).filter((p) => preCat(p) === "done").length;
  const preSignedN = (d?.projects || []).filter((p) => preCat(p) === "signed").length;
  const preAllN = preOpenN + preDoneN + preSignedN;
  const [cancelledN, setCancelledN] = useState(null);
  useEffect(() => { if (!presign) api.get("/design/v3/projects/deleted").then(({ data }) => setCancelledN(data.total)).catch(() => setCancelledN(null)); }, [api, presign, d]); // eslint-disable-line
  const unassignedN = all.filter((p) => p.assign?.missing?.length).length;
  /* 低频筛选折叠：签约前后 / 阶段任务逾期 / 停滞 / 待分配等项目收进第二行。
     有任一项生效时强制展开 —— 否则会出现「筛了但界面上看不到筛了什么」。
     计数与顶部「清除筛选」的总数分开，避免两个数字不一致让人困惑。 */
  const [moreOpen, setMoreOpen] = useState(false);
  // 承诺日筛选（target=）计入「更多筛选」的生效数量：它只由总览蓝卡下钻带入，
  // 不放进顶部常用筛选条（业务日常用的是「阶段预计」和「阶段任务逾期」两列），
  // 但**必须**参与计数与「清除筛选」，否则用户从蓝卡点进来会看不到自己处在一条隐藏筛选里。
  const moreN = [f.phase !== "all", f.legacy === "overdue", f.stale === "1", f.unassigned === "1", !!f.prog, !!f.signLv, !!f.dsf, f.target !== "all"].filter(Boolean).length;
  const showMore = moreOpen || moreN > 0;
  const activeFilters = [f.branch !== "all", f.status !== "all", f.group !== "all", f.owner !== "all", !!f.cd, f.phase !== "all", f.deadline !== "all", f.target !== "all", f.legacy === "overdue", f.stale === "1", f.unassigned === "1", !!f.prog, !!f.dsf, !!f.sort, !!f.q, !!f.signLv].filter(Boolean).length;
  const activeN = (d?.projects || []).length - handedN - doneN - preN - pausedN;
  /* 2026-10-19（用户需求）：原「进行中 / 暂停项目 / 已移交工程部 / 已取消 / 签约前营销方案」
     五个状态页签并排约占 400px，第一行筛选因此被挤到换行（用户截图红框处）。
     合并为单个下拉后与 2026-10-18 分公司页签的改法保持一致：
     1) 选中项沿用原页签的强调色（进行中紫 / 暂停琥珀 / 移交绿 / 已取消灰 / 签约前琥珀），
        图标随当前列表切换，仍能一眼看出在哪个列表；
     2) 计数写进选项文本，切换即筛选（签约前是独立路由，选中后跳转而不是改 URL 筛选）；
     3) 原 dv3-list-* testid 用 sr-only 镜像保留，既有自动化读取当前列表与计数不受影响。 */
  const listTone = presign || f.list === "presign" ? "border-amber-400 bg-amber-50 text-amber-800"
    : f.list === "paused" ? "border-amber-400 bg-amber-50 text-amber-800"
      : f.list === "handed" ? "border-emerald-400 bg-emerald-50 text-emerald-800"
        : f.list === "completed" ? "border-teal-400 bg-teal-50 text-teal-800"
          : f.list === "cancelled" ? "border-slate-400 bg-slate-100 text-slate-700"
            : "border-violet-400 bg-violet-50 text-violet-800";
  const ListIcon = presign || f.list === "presign" ? Megaphone : f.list === "paused" ? PauseCircle : f.list === "handed" ? HardHat : f.list === "completed" ? BadgeCheck : f.list === "cancelled" ? XCircle : f.list === "contracts" ? FileSignature : Layers;
  const onListChange = (e) => {
    const v = e.target.value;
    if (v === "presign") { if (!presign) nav("/design/presign"); return; } // 签约前列表是独立路由，切过去而不是改筛选
    if (v === "active" && presign) { nav("/design/projects"); return; }   // 从签约前列表切回已签约项目
    setF("list", v);
  };
  /* 行点击分流（2026-10-20 用户需求「已签约的也要展示签约前方案记录」）：
     recordOnly = 本人确实做过该项目的签约前方案，但按 visible_pids 已无项目可见性
     （签约时 pre_consult 收口所致）。这类行**只打开只读记录弹窗**，不进项目抽屉 ——
     抽屉里遍布编辑入口，而写权限收口在 assert_visible，放进去只会处处 403。
     其余行行为不变。 */
  const openPresignRecord = (id) => setRecordPid(id);
  const open = (id) => {
    const row = (d?.projects || []).find((x) => x.id === id);
    if (presign && row?.presignInfo?.recordOnly) return openPresignRecord(id);
    nav(`${base}/${id}${window.location.search}`);
  };
  // 打开某项目的抽屉并直达指定交付物（表格芯片 / 矩阵格子共用同一入口，靠 ?disc=key 让抽屉定位）
  const openDisc = (id, key) => { const n = new URLSearchParams(window.location.search); n.set("disc", key); nav(`${base}/${id}?${n}`); };
  const close = () => { const n = new URLSearchParams(window.location.search); n.delete("disc"); const s = n.toString(); nav(`${base}${s ? `?${s}` : ""}`); loadUnread(); };
  // 角标点进去直达「评论」页签（不改动其它已有筛选参数）
  const openComments = (id) => { const n = new URLSearchParams(window.location.search); n.delete("disc"); n.set("tab", "comments"); nav(`${base}/${id}?${n}`); };
  if (!meta) return <div className="space-y-4" data-testid="dv3-loading"><CardSkeleton count={4} /><div className="rounded-lg bg-white border border-slate-200"><TableSkeleton rows={6} cols={5} /></div></div>;
  if (meta.error) return <Denied status={meta?.status} />;
  return (
    /* page-fill（2026-10-16）：矩阵 / 表格视图下让页面根撑满内容区，表格卡片再用 .card-fill 吃掉
       剩余高度并内部自滚 —— 这样表头才能吸顶（sticky 必须相对「有纵向滚动量」的滚动容器才生效，
       容器高度不限时它没有滚动量，表头照样随页面滚走）。同时页面不再出现「整页滚动 + 表格内部滚动」
       两层滚动嵌套。其余视图（卡片 / 看板）不加，保持内容流式排布。
       见 index.css「页面级撑满剩余高度」一节。 */
    <div className={`space-y-3${(view === "matrix" || view === "table") && !listOnly ? " page-fill" : ""}`} data-testid={presign ? "dv3-presign" : "dv3-projects"} data-mode={mode}>
      {/* 2026-10-18 顶部工具区重构（用户需求）：
          原先「三个操作按钮」「项目状态页签」「筛选 / 搜索」各占一行，1280px 下筛选行还会再折成两行，
          矩阵实际可视高度只剩约 341px。现在把三块收进同一张工具卡：
          1) 常规桌面固定为两行：第一行是项目页签、分公司、状态与负责人；
             第二行是节点、更多筛选、搜索、排序、列设置、视图与操作按钮；
          2) 1280px 下操作按钮只显示图标和提示，1440px 起恢复完整文字，避免按钮组被拆行；
          3) 操作按钮用同一容器成组，既减少视觉碎片，也保留「产物时间设置 / 类型审核 / 新建」的权限差异。
          搜索、筛选与列设置的 testid 全部保留，避免破坏既有 E2E 契约。 */}
      <button type="button" className="md:hidden min-h-11 w-full rounded-lg border border-slate-200 bg-white px-4 py-3 flex items-center justify-between text-sm font-semibold text-slate-700" aria-expanded={mobileFiltersOpen} aria-controls="dv3-filters-panel" data-testid="dv3-mobile-filter-toggle" onClick={() => setMobileFiltersOpen((open) => !open)}>
        <span>{T("筛选")} / {T("视图")}{activeFilters > 0 ? ` (${activeFilters})` : ""}</span>
        <span>{mobileFiltersOpen ? T("收起") : T("展开")}</span>
      </button>
      <section id="dv3-filters-panel" className={`min-w-0 rounded-lg border border-slate-200 bg-white p-2 ${mobileFiltersOpen ? "block" : "hidden"} md:block`} data-testid="dv3-filters">
        <V3Header title={presign ? T("签约前营销方案") : T("项目管理")} />
        {/* 原布局在 1280px 下搜索会单独落到第三行，矩阵可视高度被压缩。
            这里固定为两条功能带：第一行负责项目范围与主筛选，第二行负责检索和操作；
            桌面宽度不足时两条带各自在内部滚动或换行，页面本身不横向溢出。
            控件与 testid 不变，移动端仍由各容器内部滚动兜底。 */}
        <div className="min-w-0 space-y-1.5" data-testid="dv3-toolbar-row">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 min-[1600px]:flex-nowrap">
          {/* 2026-10-19（用户需求）：五个状态页签合并成单个下拉，原横向滚动容器与内部滚动兜底一并去掉。
              下拉保留 dv3-list-tabs 容器 testid；当前列表 / 计数的 dv3-list-* 用 sr-only 镜像输出，
              这样按 testid 读取「当前列表」「数量」的自动化不用改。
              不设 w-full：w-full 会让它自己独占一行，把「全部状态」等筛选挤到下一行，
              与「合并成一个下拉、少占一行」的初衷相反。窄屏才满宽，保证触屏可点。

              2026-10-20（修「文字被截断」）：原固定宽 172px，实测扣掉 pl-7 / pr-7 后文字可用宽度仅 116px，
              而「签约前营销方案（7）」在 12px/600 下量得 115px，「全部分公司」也同理被 136px 裁掉尾字；
              原生下拉箭头还要再挤占右内边距，于是必然出现「签约前营销方…」。
              改为宽度随选项自适应（实测中方案 193px、英文 Pre-contract Concepts 253px）、
              仅保留一个最小宽度兜底：宽度取所有选项里最长的一项，
              所以切换列表 / 计数变化时宽度稳定不跳动，也不会因为多语言文案变长而再次截断。 */}
          <label className="relative min-w-0 shrink-0 max-sm:w-full" title={T("切换项目列表")} data-testid="dv3-list-tabs">
            <ListIcon className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5" />
            <select data-testid="dv3-list-select" value={presign ? "presign" : f.list} onChange={onListChange}
              className={`h-8 w-full cursor-pointer rounded-md border bg-white pl-7 pr-7 text-xs font-semibold sm:w-auto sm:min-w-[180px] ${listTone}`}>
              {presign ? <>
                {/* 签约前菜单现在含三档（未完成 / 已完成 / 已签约留痕），计数用合计 preAllN；
                    分类细看走右侧分段按钮，切列表仍回「已签约项目」。 */}
                <option value="presign">{T("签约前营销方案")}（{preAllN}）</option>
                <option value="active">{T("已签约项目")}（{activeN}）</option>
              </> : <>
                <option value="active">{T("进行中")}（{activeN}）</option>
                <option value="paused">{T("暂停项目")}（{pausedN}）</option>
                <option value="handed">{T("已移交工程部")}（{handedN}）</option>
                {/* 2026-10-20 用户需求「有的项目不需要移交施工，设计完了就直接结束」：
                    结项项目单列一档，不再混在进行中。 */}
                <option value="completed">{T("已结项")}（{doneN}）</option>
                <option value="cancelled">{T("已取消")}（{cancelledN ?? "—"}）</option>
                {canHandoffContracts && <option value="contracts">{T("待转设计合同")}</option>}
                {/* 跨菜单入口：指向签约前页面，计数同样改为三档合计，
                    否则点进去会看到比下拉数字更多的行（已签约留痕）。 */}
                <option value="presign">{T("签约前营销方案")}（{preAllN}）</option>
              </>}
            </select>
            <span className="sr-only" aria-hidden="true">
              <span data-testid="dv3-list-active" data-count={activeN} />
              <span data-testid="dv3-list-paused" data-count={pausedN} />
              <span data-testid="dv3-list-handed" data-count={handedN} />
              <span data-testid="dv3-list-completed" data-count={doneN} />
              <span data-testid="dv3-list-cancelled" data-count={cancelledN ?? ""} />
              <span data-testid="dv3-list-presign" data-count={preAllN} />
              <span data-testid="dv3-list-to-presign" />
              <span data-testid="dv3-list-to-active" />
              <span data-testid="dv3-list-active-count">{activeN}</span>
              <span data-testid="dv3-list-paused-count">{pausedN}</span>
              <span data-testid="dv3-list-handed-count">{handedN}</span>
              <span data-testid="dv3-list-completed-count">{doneN}</span>
              <span data-testid="dv3-list-cancelled-count">{cancelledN ?? "—"}</span>
              <span data-testid="dv3-list-presign-count">{preAllN}</span>
            </span>
          </label>
          {/* 签约前营销方案分类筛选（2026-10-20 用户需求）。
              需求原话：「在签约前营销方案中显示已完成..未完成的...已签约的列表……
              只是在签约前营销方案中增加分类的筛选，展示对应的列表」。
              四档含义（计数来自后端 presignInfo.category，与列表行集同源）：
                全部   = 三档合计（含已签约留痕，便于一次看完整条签约前链路）
                未完成 = 仍在签约前、方案还没交 → 待办
                已完成 = 仍在签约前、方案已交客户、还在等签约 → 待签约
                已签约 = 已转签约后，但明确做过签约前方案 → 只读留痕，供追溯「当初谁做的」
              用分段按钮而不是下拉：四个计数要同时可见，用户点一下就切换，不用展开再找。 */}
          {presign && (
            <div className="inline-flex shrink-0 items-center rounded-lg bg-slate-100 p-0.5 max-sm:w-full" data-testid="dv3-presign-cats" role="tablist" aria-label={T("签约前方案分类")}>
              {[["all", T("全部"), preAllN], ["open", T("未完成"), preOpenN], ["done", T("已完成"), preDoneN], ["signed", T("已签约"), preSignedN]].map(([k, l, n]) => (
                <button key={k} type="button" role="tab" aria-selected={preCatTab === k} data-testid={`dv3-presign-cat-${k}`} data-count={n}
                  onClick={() => setF("pcat", k)}
                  className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 flex-1 md:flex-none whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-semibold transition-colors ${preCatTab === k
                    ? (k === "open" ? "bg-white text-amber-700 shadow" : k === "done" ? "bg-white text-teal-700 shadow" : k === "signed" ? "bg-white text-emerald-700 shadow" : "bg-white text-slate-900 shadow")
                    : "text-slate-500 hover:text-slate-700"}`}>
                  {l}<span className="ml-1 tabular-nums text-[10px] opacity-70">{n}</span>
                </button>
              ))}
              <span className="sr-only" data-testid="dv3-presign-cat-current" data-value={preCatTab} />
            </div>
          )}
          {/* 分公司筛选（2026-10-18 用户补充）：原四个页签横向占 276px，第一组筛选因此换行。
              改为单个下拉后保留 dv3-branch-tabs 容器契约与当前值对应的
              dv3-branch-* testid，既不破坏读取当前分公司的自动化，也减少页面占用。
              2026-10-19 修正：原 w-full + min-[1600px] 才给固定宽，导致 1280~1599 区间它独占一行，
              与状态下拉一起把「全部状态」等筛选挤到第三行；现改为常驻固定宽、仅窄屏满宽。
              2026-10-20（同上一条「文字被截断」）：136px 扣掉内边距后仅剩 80px，
              而「全部分公司」需 60px、选中「洛杉矶」等三项也不宽裕，英文 All branches / NorCal Bay Area
              更要 173px；改成宽度随选项自适应 + 最小宽度兜底，避免任何语言下被裁字。 */}
          <label className="relative min-w-0 shrink-0 max-sm:w-full" title={T("按分公司筛选项目")}>
            <MapPin className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
            <select data-testid="dv3-branch-tabs" value={f.branch} onChange={(e) => setF("branch", e.target.value)} className={`h-8 w-full cursor-pointer rounded-md border bg-white pl-7 pr-7 text-xs font-semibold sm:w-auto sm:min-w-[128px] ${f.branch !== "all" ? "border-violet-400 bg-violet-50 text-violet-800" : "border-slate-200 text-slate-600"}`}>
              {BR.map(([k, l]) => <option key={k} value={k}>{T(l)}</option>)}
            </select>
            <span className="sr-only" data-testid={`dv3-branch-${f.branch}`} aria-hidden="true" data-value={f.branch} />
          </label>
          {!listOnly && <>
            <select data-testid="dv3-filter-status" value={f.group !== "all" ? `g:${T(f.group)}` : f.status} onChange={(e) => { const v = e.target.value; if (v.startsWith("g:")) { setF("status", "all"); setF("group", v.slice(2)); } else { setF("group", "all"); setF("status", v); } }} className="h-8 shrink-0 rounded-md border border-slate-200 bg-white px-2 text-xs xl:max-w-[142px]">
              <option value="all">{T("全部状态")}</option>
              {["签约前", "前期", "规划", "建筑"].map((g) => <option key={g} value={`g:${g}`}>{T("— {0}阶段（全部）", [T(g)])}</option>)}
              {meta.workStatuses.filter((s) => !s.hidden && s.key !== "handed_off").map((s) => <option key={s.key} value={s.key}>{T(s.name)}</option>)}
            </select>
            <select data-testid="dv3-filter-owner" value={f.owner} onChange={(e) => setF("owner", e.target.value)} className="h-8 shrink-0 rounded-md border border-slate-200 bg-white px-2 text-xs xl:max-w-[126px]"><option value="all">{T("全部负责人")}</option>{owners.map((o) => <option key={o} value={o}>{o}</option>)}</select>
            {f.cd && <button type="button" data-testid="dv3-filter-cd-chip" onClick={() => setF("cd", "")} className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md border border-teal-300 bg-teal-50 px-2 text-xs font-semibold text-teal-800">{T("中国建筑设计师：{0}", [f.cd === "__none" ? T("未指派") : f.cd])}<X className="h-3 w-3" /></button>}
            <select data-testid="dv3-filter-deadline" value={f.deadline} onChange={(e) => setF("deadline", e.target.value)} className={`h-8 shrink-0 rounded-md border bg-white px-2 text-xs ${f.deadline !== "all" ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}><option value="all">{T("全部阶段预计")}</option><option value="attention">{T("阶段预计临期 + 逾期")}</option><option value="overdue">{T("阶段预计已逾期")}</option><option value="due_soon">{T("阶段预计 ≤3 天临期")}</option><option value="ok">{T("阶段预计正常")}</option></select>
            {/* 低频筛选仍按需展开；有生效项时自动打开，避免「筛了但界面看不到」。 */}
            <button type="button" data-testid="dv3-filter-more" onClick={() => setMoreOpen((v) => !v)} aria-expanded={showMore}
              className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex h-8 shrink-0 items-center gap-1 rounded-md border px-2 text-xs font-semibold ${moreN ? "border-violet-400 bg-violet-50 text-violet-800" : "border-slate-200 bg-white text-slate-600"}`}>
              <SlidersHorizontal className="h-3.5 w-3.5" />{T("更多筛选")}{moreN > 0 && <span className="ml-0.5 rounded-full bg-violet-600 px-1.5 text-[10px] text-white" data-testid="dv3-filter-more-count">{moreN}</span>}
              <ChevronDown className={`h-3 w-3 transition-transform ${showMore ? "rotate-180" : ""}`} />
            </button>
          </>}
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 min-[1280px]:flex-nowrap">
          <div className="relative min-w-0 shrink-0 max-sm:w-full">
            <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
            <input data-testid="dv3-search" value={f.q} onChange={(e) => setF("q", e.target.value)} placeholder={T("搜索地址 / 城市 / 业主 / 编号")} aria-busy={qStale ? "true" : undefined} className={`h-8 w-full rounded-md border bg-white pl-7 pr-7 text-xs sm:w-60 xl:w-64 ${qStale ? "border-violet-300" : "border-slate-200"}`} />
            {f.q && <button type="button" data-testid="dv3-search-clear" onClick={() => setF("q", "")} title={T("清空搜索")} className="absolute right-1.5 top-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-200 text-slate-600 hover:bg-slate-300"><X className="h-2.5 w-2.5" /></button>}
          </div>
          <label className="inline-flex min-w-0 shrink-0 items-center gap-1 text-xs text-slate-500 max-sm:w-full" title={T("排序：按设计阶段耗时（签署日起自然天数）等排列")}><ArrowDownUp className="h-3.5 w-3.5" />
            <select data-testid="dv3-sort-select" value={f.sort} onChange={(e) => setF("sort", e.target.value)} className={`h-8 w-full rounded-md border bg-white px-2 text-xs sm:w-[180px] ${f.sort ? "border-violet-400 bg-violet-50 font-semibold text-violet-800" : "border-slate-200"}`}>
              <option value="">{T(DEFAULT_SORT_LABEL)}</option>
              <optgroup label={T("按耗时")}>{SORTS.filter(([v]) => v.startsWith("sign_")).map(([v, l]) => <option key={v} value={v}>{T(l)}</option>)}</optgroup>
              <optgroup label={T("其他排序")}>{SORTS.filter(([v]) => !v.startsWith("sign_")).map(([v, l]) => <option key={v} value={v}>{T(l)}</option>)}</optgroup>
            </select>
          </label>
          {activeFilters > 0 && <button type="button" data-testid="dv3-filter-clear" onClick={() => setMany({ branch: "", status: "", group: "", owner: "", cd: "", risk: "", phase: "", deadline: "", target: "", legacy: "", stale: "", unassigned: "", prog: "", dsf: "", sort: "", q: "", signLv: "" })} className="inline-flex h-8 shrink-0 items-center gap-1 text-xs font-semibold text-violet-700 hover:underline"><X className="h-3 w-3" />{T("清除筛选 ({0})", [activeFilters])}</button>}
          {!listOnly && <div className="flex shrink-0 items-center gap-1.5 sm:ml-auto max-sm:w-full">
            {/* 表格与矩阵共用同一组信息列和同一份偏好；卡片 / 看板没有这 7 列，不显示入口。 */}
            {(view === "table" || view === "matrix") && <ColSettings hidden={hiddenCols} onToggle={(k) => setColsByUser((v) => (v.includes(k) ? v.filter((x) => x !== k) : [...v, k]))} onReset={() => setColsByUser([])} />}
            <div className="inline-flex shrink-0 rounded-lg bg-slate-100 p-0.5" data-testid="dv3-view-toggle">
              {[["matrix", Grid3X3, T("矩阵")], ["cards", LayoutGrid, T("卡片")], ["table", Table2, T("表格")], ["board", Kanban, T("看板")]].map(([k, I, l]) => <button key={k} data-testid={`dv3-view-${k}`} title={l} aria-label={l} aria-pressed={view === k} onClick={() => setView(k)} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-semibold ${view === k ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}><I className="h-3.5 w-3.5" /><span className="hidden min-[1920px]:inline">{l}</span></button>)}
            </div>
          </div>}
          {((meta.isManager && !presign) || (meta.isManager && !listOnly && !pausedTab && !presign) || (meta.canEdit && !listOnly && !pausedTab && !presign)) && (
            <div className="flex min-w-0 shrink-0 items-center gap-0.5 overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 p-0.5 max-sm:w-full" data-testid="dv3-header-actions">
              {meta.isManager && !presign && <Button size="sm" variant="ghost" data-testid="dv3-disc-cfg-btn" onClick={() => setDiscCfg(true)} title={T("产物时间设置")} aria-label={T("产物时间设置")} className="h-7 px-2 text-xs text-amber-700 hover:bg-amber-50"><Timer className="h-3.5 w-3.5 min-[1440px]:mr-1" /><span className="hidden min-[1440px]:inline">{T("产物时间设置")}</span></Button>}
              {meta.isManager && !listOnly && !pausedTab && !presign && <Button size="sm" variant="ghost" data-testid="dv3-type-review-btn" onClick={() => setTypeReview(true)} title={T("类型审核")} aria-label={T("类型审核")} className="h-7 px-2 text-xs text-violet-700 hover:bg-violet-50"><Tag className="h-3.5 w-3.5 min-[1440px]:mr-1" /><span className="hidden min-[1440px]:inline">{T("类型审核")}</span></Button>}
              {meta.canEdit && !listOnly && !pausedTab && !presign && <Button size="sm" data-testid="dv3-new-project" onClick={() => setCreating(true)} title={T("新建设计项目")} aria-label={T("新建设计项目")} className="h-7 bg-slate-900 px-2.5 text-xs hover:bg-slate-700"><Plus className="h-3.5 w-3.5 min-[1440px]:mr-1" /><span className="hidden min-[1440px]:inline">{T("新建设计项目")}</span></Button>}
            </div>
          )}
          </div>
        </div>
        {!listOnly && showMore && (
          <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2" data-testid="dv3-filters-more">
            <select data-testid="dv3-filter-prog" value={f.prog} onChange={(e) => setF("prog", e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"><option value="">{T("全部进度")}</option>{PROG_RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <select data-testid="dv3-filter-sign" value={f.signLv} onChange={(e) => setF("signLv", e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"><option value="">{T("全部耗时")}</option>{SIGN_LV.map(([v, l]) => <option key={v} value={v}>{T(l)}</option>)}</select>
            <select data-testid="dv3-filter-dsf" value={f.dsf} onChange={(e) => setF("dsf", e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"><option value="">{T("全部交付物状态")}</option>{Object.entries(meta.discStatusMeta || {}).filter(([k]) => k !== "not_started").map(([k, m]) => <option key={k} value={k}>{T("含「{0}」", [T(m.label)])}</option>)}</select>
            {!presign && <select data-testid="dv3-filter-phase" value={f.phase} onChange={(e) => setF("phase", e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"><option value="all">{T("签约前后")}</option><option value="pre_sign">{T("签约前·营销方案")}</option><option value="signed">{T("签约后")}</option></select>}
            {/* 逾期筛选按钮（2026-10-29 扩口径）：原来只认任务级 legacyOverdue，
                用户报「详情 13 项逾期却进不了这个名单」。现在任务级与节点检查级取并集，
                title 里写明两种来源，避免用户以为这是同一个数字。 */}
            <button type="button" data-testid="dv3-filter-legacy" onClick={() => setF("legacy", f.legacy === "overdue" ? "all" : "overdue")} title={T("含两类逾期：阶段任务逾期（任务级，与每日晨报同口径）＋ 节点检查逾期（阶段 + 结构清单；阶段内部的子任务/交付物/机构不单独计时）。")} className={`inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs font-semibold ${f.legacy === "overdue" ? "border-red-400 bg-red-50 text-red-700" : "border-slate-200 bg-white text-slate-600"}`}><Layers className="h-3.5 w-3.5" />{T("阶段任务逾期")}</button>
            {/* 承诺日筛选（2026-10-27）：只在从总览蓝卡「节点逾期 / 临期」下钻时出现。
                显式做成可见 chip 而不是静默生效的 URL 参数 —— 用户点蓝卡进来若看到
                「只列出 3 个项目」却找不到任何筛选痕迹，会以为列表丢数据。
                文案用「承诺日」把它和「阶段预计」（阶段排期末端）在字面上就区分开。 */}
            {f.target !== "all" && (
              <button type="button" data-testid="dv3-filter-target-chip" onClick={() => setF("target", "")} title={T("总览蓝卡下钻带入：按项目承诺结束日统计，与「阶段预计」不是同一个字段")}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-red-300 bg-red-50 px-2 text-xs font-semibold text-red-700">
                <CalendarClock className="h-3.5 w-3.5" />{f.target === "attention" ? T("承诺日临期 + 逾期") : f.target === "overdue" ? T("承诺日已逾期") : T("承诺日 ≤3 天临期")}<X className="h-3 w-3" />
              </button>
            )}
            <button type="button" data-testid="dv3-filter-stale" onClick={() => setF("stale", f.stale === "1" ? "" : "1")} className={`inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs font-semibold ${f.stale === "1" ? "border-amber-400 bg-amber-50 text-amber-800" : "border-slate-200 bg-white text-slate-600"}`}><PauseCircle className="h-3.5 w-3.5" />{T("停滞 {0} 天+", [meta.staleDays || 14])}</button>
            {meta.isManager && <button type="button" data-testid="dv3-filter-unassigned" onClick={() => setF("unassigned", f.unassigned === "1" ? "" : "1")} className={`inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs font-semibold ${f.unassigned === "1" ? "border-red-400 bg-red-50 text-red-700" : "border-slate-200 bg-white text-slate-600"}`}><UserX className="h-3.5 w-3.5" />{T("待分配设计师")}{unassignedN > 0 && <span className="ml-0.5 rounded-full bg-red-600 px-1.5 text-[10px] text-white" data-testid="dv3-filter-unassigned-count">{unassignedN}</span>}</button>}
          </div>
        )}
      </section>
      {/* 查看范围提示（2026-10-21，需求：Arthi 看全部 Norcal 项目）。
          后端在账号设了 designScope = 本分公司 / 全部时返回 scopeLabel，列表会混入
          「本人并非参与人」的项目（行上带 readOnlyScope，只读）。这里常驻一条说明，
          避免用户以为「为什么这些项目改不了」是功能故障。 */}
      {!presign && d?.scopeLabel && (
        <div className="flex items-start gap-1.5 rounded-lg border border-violet-100 bg-violet-50 px-3 py-2 text-[11px] text-violet-800" data-testid="dv3-scope-notice">
          <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{T("当前查看范围：{0}。非本人参与的项目可查看、不可修改。", [d.scopeLabel])}</span>
        </div>
      )}
      {presign && <PresignStatsCard isManager={meta.isManager} />}
      {contractTab ? <DesignContractHandoff api={api} query={f.q} onChanged={load} />
        : cancelledTab ? <CancelledList api={api} meta={meta} branch={f.branch} q={f.q} onOpen={open} onChanged={load} />
        : completedTab ? <CompletedList api={api} meta={meta} branch={f.branch} q={f.q} onOpen={open} onChanged={load} />
        : handed ? <HandedOffList api={api} meta={meta} branch={f.branch} q={f.q} onOpen={open} onlyMissing={f.receipt === "missing"} setOnlyMissing={(v) => setF("receipt", v ? "missing" : "")} />
        : !d ? <div className="h-40 flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        : d.error ? <div className="p-6 text-sm text-red-600" data-testid="dv3-projects-error">{errTextOf(d.error)}</div>
        : list.length === 0 ? <div className="p-10 text-center text-sm text-slate-400 bg-white rounded-lg border border-slate-200" data-testid="dv3-empty">{T("没有符合筛选条件的项目")}</div>
        : view === "cards" ? <><QuickFilterBar mobileOpen={mobileFiltersOpen} all={all} f={f} setF={setF} setMany={setMany} owners={owners} cnOwners={cnOwners} meta={meta} /><Cards list={shownList} footer={listFooter} meta={meta} onOpen={open} onFilter={setMany} unread={cUnread} onOpenComments={openComments} presign={presign} /></>
        : view === "table" ? <TableView list={shownList} footer={listFooter} all={all} f={f} setF={setF} setMany={setMany} owners={owners} cnOwners={cnOwners} meta={meta} onOpen={open} onOpenDisc={openDisc} onDelete={canDelete ? setDeleting : null} hidden={hiddenCols} presign={presign} />
        : view === "matrix" ? <><QuickFilterBar mobileOpen={mobileFiltersOpen} all={all} f={f} setF={setF} setMany={setMany} owners={owners} cnOwners={cnOwners} meta={meta} /><MatrixView list={shownList} footer={listFooter} all={all} meta={meta} api={api} onOpen={open} onOpenStage={(pid, key) => {
          /* recordOnly 行（已签约留痕、本人已无项目可见性）不能进阶段会话：
             会话抽屉依赖项目写 / 读权限，进去只会 403。点格子改为打开只读记录弹窗，
             与点项目名一致。
             同理（2026-10-21）：行级只读（p.readOnlyScope，账号查看范围放宽进来的项目）
             也不进阶段会话 —— 会话里能发消息，而后端写闸门会 403。点格子统一打开
             项目详情的只读视图，与点项目名一致。 */
          const row = (d?.projects || []).find((x) => x.id === pid);
          if (presign && row?.presignInfo?.recordOnly) return open(pid);
          if (row?.readOnlyScope) return open(pid);
          setChatStage({ pid, stageKey: key });
        }} onSaved={load} hidden={hiddenCols} onFilter={setMany} unread={cUnread} onOpenComments={openComments} presign={presign} /></>
        : <><QuickFilterBar mobileOpen={mobileFiltersOpen} all={all} f={f} setF={setF} setMany={setMany} owners={owners} cnOwners={cnOwners} meta={meta} /><Board list={list} meta={meta} onOpen={open} api={api} onChanged={load} /></>}
      {/* 2026-10-19 用户需求：旧产物会话抽屉整体下线，产物只做展示。
          表格产物芯片 / 详情交付物卡片改为「打开项目详情并定位到该产物」，
          不再跳到独立的产物聊天窗口。reloadKey 仍保留：阶段会话里改完产物后，
          详情「会话产物」只读区会重拉。 */}
      {pid && <ProjectDrawer pid={pid} meta={meta} initialTab={sp.get("tab") || undefined} initialDisc={sp.get("disc") || undefined} onClose={close} onChanged={load} reloadKey={detailRefresh} />}
      {chatStage && <StageChatDrawer key={`${chatStage.pid}:${chatStage.stageKey}`} pid={chatStage.pid} stageKey={chatStage.stageKey} meta={meta}
        onChanged={() => { load(); loadUnread(); setDetailRefresh((x) => x + 1); }}
        onOpenProject={() => { const q = chatStage; setChatStage(null); open(q.pid); }}
        onClose={() => { setChatStage(null); load(); loadUnread(); setDetailRefresh((x) => x + 1); }} />}
      {discCfg && <DiscCfgDialog api={api} onClose={() => setDiscCfg(false)} />}
      {recordPid && <PresignRecordDialog pid={recordPid} api={api} onClose={() => setRecordPid(null)} />}
      {creating && <NewProjectDialog meta={meta} api={api} branch={f.branch} onClose={() => setCreating(false)} onCreated={(p) => { setCreating(false); load(); open(p.id); }} />}
      {typeReview && <TypeReviewDialog api={api} onClose={() => setTypeReview(false)} onApplied={load} />}
      {deleting && <DeleteProjectDialog p={deleting} api={api} onClose={() => setDeleting(null)} onDone={() => { setDeleting(null); load(); }} />}
    </div>
  );
}

/** 卡片 / 表格：一句话现状 + 下一步日期（抽屉头部填写） */
const BriefLine = ({ p, tid }) => p.statusBrief ? (
  <p className="mt-0.5 text-[11px] text-sky-800 flex items-center gap-1 min-w-0" data-testid={tid} title={`${p.statusBrief}${p.statusBriefBy ? ` · ${p.statusBriefBy}` : ""}`}>
    <MessageSquareText className="h-3 w-3 text-sky-500 shrink-0" /><span className="truncate">{p.statusBrief}</span>
    {p.statusBriefNextDate && <span className="shrink-0 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1" data-testid={`${tid}-next`}>{T("下一步 {0}", [fmtD(p.statusBriefNextDate)])}</span>}
  </p>
) : null;

/* 签约前方案分类芯片（2026-10-20 用户需求）。
   在「签约前营销方案」菜单的卡片 / 表格 / 矩阵行里直接标出这一行属于
   未完成 / 已完成 / 已签约，并把「谁做的签约前方案」显示出来 ——
   用户的核心诉求就是「哪怕已签约，也要能看出这个方案是谁做的」，
   所以已签约行的芯片同时带设计师名，而不只是一个状态点。
   非签约前菜单（presign=false）不渲染，避免在项目管理里多出无意义的标签。 */
const PresignChip = ({ p, presign, tid }) => {
  if (!presign || !p.presignInfo) return null;
  const c = p.presignInfo.category;
  const tone = c === "open" ? "bg-amber-50 text-amber-800 border-amber-200"
    : c === "done" ? "bg-teal-50 text-teal-800 border-teal-200"
      : "bg-emerald-50 text-emerald-800 border-emerald-200";
  const label = c === "open" ? T("未完成") : c === "done" ? T("已完成") : T("已签约");
  return (
    <span data-testid={tid} data-category={c} data-designer={p.presignInfo.designer || ""}
      title={p.presignInfo.designer ? T("签约前方案设计师：{0}", [p.presignInfo.designer]) : ""}
      className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${tone}`}>
      <Megaphone className="h-2.5 w-2.5" />{label}
      {p.presignInfo.designer && <span className="font-semibold opacity-80">· {p.presignInfo.designer}</span>}
    </span>
  );
};

const Cards = ({ list, meta, onOpen, onFilter, unread = {}, onOpenComments, presign = false, footer = null }) => (
  <>
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3" data-testid="dv3-cards">
    {list.map((p) => (
      <div key={p.id} role="button" tabIndex={0} data-testid={`dv3-card-${p.id}`} onClick={() => onOpen(p.id)} onKeyDown={(e) => { if (e.key === "Enter") onOpen(p.id); }} className="text-left bg-white rounded-lg border border-slate-200 p-4 hover:border-violet-400 hover:shadow-md transition-all group cursor-pointer">
        <div className="flex items-start gap-2.5">
          <Ring v={p.progress} size={46} stroke={5} th={meta.ringThresholds} tid={`dv3-card-ring-${p.id}`} className="mt-0.5" />
          <div className="flex-1 min-w-0"><p className="text-sm font-bold text-slate-900 truncate group-hover:text-violet-700">{p.address}</p><p className="text-[11px] text-slate-500 flex items-center gap-1 truncate"><MapPin className="h-3 w-3" />{p.city || "—"} · {T(meta.branches[p.branch]?.name)} · {p.projectId}</p><BriefLine p={p} tid={`dv3-card-brief-${p.id}`} /><div className="mt-1 empty:hidden"><PresignChip p={p} presign={presign} tid={`dv3-card-presign-${p.id}`} /></div></div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <CommentBadge n={unread[p.id]} tid={`dv3-card-comments-${p.id}`} onClick={onOpenComments ? () => onOpenComments(p.id) : undefined} />
          </div>
        </div>
        <div className="mt-2.5 flex items-center gap-2"><WsPill ws={p.workStatus} meta={meta} tid={`dv3-card-ws-${p.id}`} onClick={onFilter ? (e) => { e.stopPropagation(); onFilter({ group: "", status: p.workStatus }); } : undefined} /><span className="text-[10px] text-slate-400 truncate">{T(p.ws?.sub)}</span><span className="ml-auto inline-flex items-center gap-1"><StaleChip p={p} tid={`dv3-card-stale-${p.id}`} /><DeadlineBadge p={p} tid={`dv3-card-deadline-${p.id}`} />{p.structSummary && <span data-testid={`dv3-card-struct-${p.id}`} className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${p.structSummary.overdue ? "bg-red-50 text-red-700 border border-red-200" : "bg-pink-50 text-pink-700 border border-pink-200"}`}>{T("结构 {0}/{1}{2}", [p.structSummary.done, p.structSummary.total, p.structSummary.overdue ? T(" · 逾期 {0}", [p.structSummary.overdue]) : ""])}</span>}</span></div>
        <div className="mt-2.5 flex flex-wrap gap-1">
          {p.disciplines.filter((x) => x.status !== "not_started").slice(0, 8).map((x) => <DsPill key={x.key} status={x.status} meta={meta} discKind={x.discKind} small />)}
          {p.disciplines.filter((x) => x.status !== "not_started").length > 8 && <span className="text-[10px] text-slate-400">+{p.disciplines.filter((x) => x.status !== "not_started").length - 8}</span>}
        </div>
        {/* 完成数分母取当前可见产物数，不写死 16：Plumbing / Mechanical / Electrical
            已合并进 Building，当前标准是 13 项；历史项目仍保留这三项原数据。 */}
        {/* 两个逾期角标分开显示，因为它们统计的对象不同（2026-10-29 新增后一个）：
              · 红色 Layers 数字 = 阶段**任务**逾期项数（任务级，与每日晨报同一口径）
              · 红色 AlertTriangle = **节点检查**逾期（阶段本身 / 交付物 / 机构 / 结构清单）
            只显示「有」或阶段数+清单数，不把两个数相加 —— 详情里那 13 项包含阶段本身
            与逐项交付物，与任务条数是两个量纲，相加会造出一个谁也对不上的数字。 */}
        <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500"><span className="inline-flex items-center gap-1.5"><span role="button" tabIndex={-1} data-testid={`dv3-card-owner-${p.id}`} title={T("点击只看此负责人")} onClick={onFilter ? (e) => { e.stopPropagation(); onFilter({ unassigned: "", owner: p.owner || "未指派" }); } : undefined} className="inline-flex items-center gap-1 hover:text-violet-700 hover:underline"><User className="h-3 w-3" />{p.owner || T("未指派")}</span><UnassignedChip p={p} tid={`dv3-card-unassigned-${p.id}`} /></span><span className="inline-flex items-center gap-1.5">{p.legacyOverdue?.count > 0 && <span className="inline-flex items-center gap-0.5 font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid={`dv3-card-legacy-${p.id}`} title={T("阶段任务逾期 {0} 项 · 最早 {1} 天（按当前阶段节点）", [p.legacyOverdue.count, p.legacyOverdue.oldestDays])}><Layers className="h-3 w-3" />{p.legacyOverdue.count}</span>}{p.nodeOverdue?.overdue && <span className="inline-flex items-center gap-0.5 font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200" data-testid={`dv3-card-node-overdue-${p.id}`} title={T("节点检查逾期：{0} 个阶段 · {1} 条结构清单 · 最早 {2} 天（与详情同一套口径；阶段内部子任务/交付物/机构不单独计时）", [p.nodeOverdue.stages, p.nodeOverdue.structural, p.nodeOverdue.oldestDays])}><AlertTriangle className="h-3 w-3" />{p.nodeOverdue.stages > 0 ? T("节点 {0}", [p.nodeOverdue.stages]) : T("清单 {0}", [p.nodeOverdue.structural])}</span>}<span>{p.stageTotalCount ? T("{0}/{1} 阶段完成{2}{3}", [p.stageDoneCount, p.stageTotalCount, p.correctionCount ? T(" · 整改 {0}", [p.correctionCount]) : "", p.stuckCount ? T(" · 阻塞 {0}", [p.stuckCount]) : ""]) : T("暂无阶段进度")}</span></span></div>
      </div>
    ))}
  </div>
  {footer}
  </>
);

/** 表头 / 快速筛选条共用的列定义（每列：label · tid · value · onSelect · base · options） */
const columnDefs = ({ all, f, setF, setMany, owners, cnOwners = [], meta }) => {
  const base = (col) => all.filter((p) => passes(p, f, col));
  const statusVal = f.group !== "all" ? `g:${f.group}` : f.status !== "all" ? f.status : "";
  const ownerVal = f.unassigned === "1" ? "__unassigned" : f.owner !== "all" ? f.owner : f.cd ? `cd:${f.cd}` : "";
  const deadlineVal = f.stale === "1" ? "__stale" : f.deadline !== "all" ? f.deadline : "";
  const wsOpts = meta.workStatuses.filter((s) => !s.hidden && s.key !== "handed_off");
  const none = T("未指派");
  return [
    /* 「项目」列 = 表格左上角交叉格：横向吸附（left-0）+ 纵向吸顶（top-0，由 TableHead 统一补），
       z 值由 TableHead 按列序给（首列最高），故此处不再自带 z-[2]，避免两个 z 工具类打架。 */
    { key: "project", label: T("项目"), tid: "dv3-th-project", className: "sticky left-0 bg-slate-50 min-w-[200px] sm:min-w-[260px] shadow-[inset_-1px_0_0_#e2e8f0]", allLabel: DEFAULT_SORT_LABEL, value: f.sort, onSelect: (v) => setF("sort", v), base: base("sort"), options: SORTS.map(([v, l]) => ({ v, l: T(l) })) },
    { key: "branch", label: T("分公司"), tid: "dv3-th-branch", value: f.branch === "all" ? "" : f.branch, onSelect: (v) => setF("branch", v), base: base("branch"), options: BR.slice(1).map(([v, l]) => ({ v, l: T(l), test: (p) => p.branch === v })) },
    { key: "status", label: T("工作状态"), tid: "dv3-th-status", value: statusVal, onSelect: (v) => (v.startsWith("g:") ? setMany({ status: "", group: v.slice(2) }) : setMany({ group: "", status: v })), base: base("status"),
      options: [...["签约前", "前期", "规划", "建筑"].map((g) => ({ v: `g:${g}`, l: T("— {0}阶段（全部）", [T(g)]), test: (p) => p.ws?.group === g })), ...wsOpts.map((s, i) => ({ v: s.key, l: T(s.name), color: WS_COLOR[s.key]?.c, test: (p) => p.workStatus === s.key, head: i === 0 }))] },
    { key: "prog", label: T("进度"), tid: "dv3-th-prog", className: "w-40", value: f.prog, onSelect: (v) => setF("prog", v), base: base("prog"), options: PROG_RANGES.map(([v, l, fn]) => ({ v, l, test: (p) => p.progress != null && fn(p.progress) })) },
    { key: "sign", label: T("耗时"), tid: "dv3-th-sign", className: "whitespace-nowrap", value: f.sort.startsWith("sign_") && !f.signLv ? `sort:${f.sort}` : f.signLv, onSelect: (v) => (v.startsWith("sort:") ? setMany({ sort: v.slice(5), signLv: "" }) : v === "" ? setMany({ signLv: "", sort: f.sort.startsWith("sign_") ? "" : f.sort }) : setF("signLv", v)), base: base("sign"),
      options: [...SORTS.filter(([v]) => v.startsWith("sign_")).map(([v, l], i) => ({ v: `sort:${v}`, l: T(l), hdr: i === 0 ? T("排序") : undefined })), ...SIGN_LV.map(([v, l, c], i) => ({ v, l: T(l), color: c, test: (p) => inSign(p, v), hdr: i === 0 ? T("只看") : undefined }))] },
    { key: "owner", label: T("负责人"), tid: "dv3-th-owner", value: ownerVal, base: base("owner"),
      onSelect: (v) => (v === "" ? setMany({ owner: "", cd: "", unassigned: "" }) : v === "__unassigned" ? setMany({ owner: "", cd: "", unassigned: "1" }) : v.startsWith("cd:") ? setMany({ unassigned: "", cd: v.slice(3) }) : setMany({ unassigned: "", owner: v })),
      options: [...(meta.isManager ? [{ v: "__unassigned", l: T("待分配设计师"), color: "#dc2626", test: (p) => p.assign?.missing?.length > 0 }] : []),
        ...owners.map((o, i) => ({ v: o, l: o, test: (p) => ownerKey(p) === o || (p.owner || none) === o, hdr: i === 0 ? T("美国负责人") : undefined })),
        ...cnOwners.map((o, i) => ({ v: `cd:${o === none ? "__none" : o}`, l: o, test: (p) => cnKey(p) === o || (p.cdDesigner || none) === o, hdr: i === 0 ? T("中国负责人") : undefined }))] },
    /* 「阶段预计」列（IT 报告全局工期设置一行的落点）。
       【2026-10-29 补列头口径说明，不加新列】IT 要求「核对 Standard End Date、
       Committed End Date、Stage forecast 与任务截止日的取值及延期叠加方式」。
       这三个日期在本系统里是三个不同东西，但列表只有两列（本列 + 标准结束日），
       承诺日（targetDate）藏在详情「管理历史承诺日与延期审批」折叠区里 —— 从列表看不到，
       于是 IT 只能拿「标准结束日」和「阶段预计」去比，越比越像数据错了。
       开发库实测（112 个项目 / 81 活跃）：两者都有日期的 67 个里，51 个完全相同、
       12 个差 1 天（承诺日按工作状态 SLA 天数自动设、阶段预计是阶段截止日累加，
       两套算法本来就可能差一天）、4 个差更多（真实顺延／延期审批造成）。

       为什么**不加第三列**：用户 2026-10-27 已定调「只显示在列表增加一个预计结束日期的
       显示，其他不管阶段的限制」。加「承诺日」列会推翻那个口径，而且承诺日由延期审批
       治理、和阶段排期是两条独立轨道，并列三列会把它们混成一条时间轴。
       所以这里只补 title 说明三者区别 + 指向详情里的承诺日，不改列结构。 */
    { key: "deadline", label: T("阶段预计"), tid: "dv3-th-deadline", value: deadlineVal, onSelect: (v) => (v === "__stale" ? setMany({ deadline: "", stale: "1" }) : setMany({ stale: "", deadline: v })), base: base("deadline"),
      title: T(COL_TITLE.deadline),
      options: [{ v: "attention", l: T("阶段预计临期 + 逾期"), color: "#d97706", test: (p) => ["overdue", "due_soon"].includes(stageForecastState(p)) }, { v: "overdue", l: T("阶段预计已逾期"), color: "#dc2626", test: (p) => stageForecastState(p) === "overdue" }, { v: "due_soon", l: T("阶段预计 ≤3 天临期"), color: "#f59e0b", test: (p) => stageForecastState(p) === "due_soon" }, { v: "ok", l: T("阶段预计正常"), color: "#16a34a", test: (p) => stageForecastState(p) === "ok" }, { v: "__stale", l: T("停滞 {0} 天+", [meta.staleDays || 14]), color: "#b45309", test: (p) => p.isStale, head: true }] },
    /* 标准结束日（2026-10-27 新增）：纯展示列，**刻意不给筛选 / 排序**。
       用户口径是「只显示在列表增加一个预计结束日期的显示，其他不管阶段的限制」：
       这一列是合同尺子（签约日 + 82 美国工作日），只让老板 / 管理员一眼看出拖了多久；
       一旦给它一个「已超期」筛选，它立刻会被当成「逾期项目」入口，
       和「阶段任务逾期」（读 design_stages.dueDate，业务真口径）混为一谈。
       故 `plain: true` —— 表头只渲染列名，不弹菜单；快速筛选条也不出这一项。

       【2026-10-29 补列头说明】IT 报告点名要求「明确历史基准逾期与当前节点逾期的
       展示区别」：37 Huntington 显示超期 810 天、306 Jackson 303 天，被误读成
       「当前阶段延误了 810 天」。数值本身没错（签约日 + 82 美国工作日），错在页面只有
       悬停 title 才解释，扫一眼看不出来。这里给列名加一个常驻 ⓘ 并把口径写进 title
       （列名本身不变，避免把 196px 的列再撑宽）；计算逻辑一行未改。 */
    { key: "std", label: T("标准结束日"), tid: "dv3-th-std", plain: true,
      title: T(COL_TITLE.std) },
    { key: "dsf", label: T("交付物"), tid: "dv3-th-dsf", value: f.dsf, onSelect: (v) => setF("dsf", v), base: base("dsf"), options: Object.entries(meta.discStatusMeta || {}).filter(([k]) => k !== "not_started").map(([k, m]) => ({ v: k, l: T("含「{0}」", [T(m.label)]), color: DS_COLOR[k]?.c, test: (p) => (p.disciplines || []).some((x) => x.status === k) })) },
  ];
};

/**
 * 表格 / 矩阵「列设置」（2026-10-17 表格；2026-10-18 扩展到矩阵）
 *
 * 可配置范围**只含「项目」之后的信息列**：分公司 / 工作状态 / 进度 / 耗时 / 负责人 / 节点 / 标准结束日。
 * （2026-10-27 新增「标准结束日」列，见 CONFIG_COLS 注释：纯展示，不参与任何判定。）
 * 矩阵复用同一组列：矩阵仍需要一眼看到项目状态和负责人；
 * 这些信息列夹在项目与交付物之间，两个视图共用偏好，避免切视图后列忽隐忽现。
 * 为什么首列「项目」与末列「交付物」不开放：
 *   1) 首列「项目」是行标识，同时 sticky left-0（表格左侧冻结列的吸附锚点，见 TableHead 注释），
 *      藏掉后行认不出是谁、表头 z 分层与冻结结构也跟着塌；
 *   2) 末列「交付物」是当前标准 13 项交付物芯片带，是业务侧横向比对交付物状态的唯一入口，
 *      与「矩阵」视图是同一件事的两种呈现，不应当被误关。
 * 换句话说：**能关的只有「中间那组信息列」**，这也正是产品截图里圈出的那一组。
 * 交付物列（表格为芯片带、矩阵为 13 项固定列）不进入开关，防止用户把唯一的状态比对入口关掉。
 *
 * 偏好（2026-10-20 改为账号级）：隐藏列以 `users/me/prefs.dv3Cols` 为准，跨设备 / 跨浏览器跟随账号；
 * 本机 localStorage（dv3_cols_v1:<用户名>）只做首屏缓存与接口失败兜底。
 * key 仍带版本号，以后要改语义直接升 v2，老偏好自然作废。
 * 浮层用 createPortal 挂到 body + fixed 定位：表格卡片是 overflow-auto 容器，
 * 内联 absolute 浮层会被裁掉（项目既有约定：浮层一律 portal 到 body）。
 */
const COL_PREF_KEY = "dv3_cols_v1";
/* 可配置信息列（2026-10-27 新增 std「标准结束日」）。
   std 放在 deadline 之后：先看阶段排期，再用合同尺子量拖了多久。
   它是**纯展示列**，默认显示，可在列设置里关掉。 */
const CONFIG_COLS = [
  ["branch", "分公司"], ["status", "工作状态"], ["prog", "进度"], ["sign", "耗时"],
  ["owner", "负责人"], ["deadline", "节点"], ["std", "标准结束日"],
];
/* 矩阵列宽 MATRIX_INFO_WIDTH 已挪到 ./MatrixInfoTh.jsx（连同「按英文最坏情况定宽」的
   完整推导过程），本文件从那里 import。搬家原因：矩阵表头要能单独挂载做单测。 */
/* 阶段列统一宽度（2026-10-22 第七轮）：所有阶段列**同一个固定宽度**，
   不再让长标签把个别列顶宽。表头超长标签改为截断（title 里保留全名）。
   宽度取 112px：96px 下「P+B 同步提交 / P+B 批+Permit」这排节点名要截得很短
   （用户反馈格子太窄），112px 刚好能放下「P+B」徽标 + 4 个汉字。
   列数取当前阶段模板，矩阵本来就横向滚动，加宽不影响左侧固定列。 */
const MATRIX_STAGE_WIDTH = 112;

const ColSettings = ({ hidden, onToggle, onReset }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!panelRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, [open]);
  const toggle = () => {
    // 位置按按钮实测 rect 算（根字号 18px，浮层严禁写死像素宽度/位置）
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    setOpen((v) => !v);
  };
  const shown = CONFIG_COLS.length - hidden.length;
  // 需求（2026-10-18）：列设置入口需要始终告诉用户当前显示多少列。
  // 原实现仅在有关闭列时显示 6/7，全显示时反而没有任何计数，和设计稿及用户截图不一致；
  // 这里常显「7/7」，隐藏列后仍能直接看出被关掉多少。
  return (
    <>
      <button ref={btnRef} type="button" onClick={toggle} data-testid="dv3-col-settings-btn"
        aria-expanded={open} className={`inline-flex h-8 items-center gap-1 text-xs border rounded-md px-2 py-1.5 font-semibold ${hidden.length ? "border-violet-400 bg-violet-50 text-violet-800" : "border-slate-200 text-slate-600"}`}>
        <Columns3 className="h-3.5 w-3.5" />{T("列设置")}
        <span className={`ml-0.5 rounded-full px-1.5 text-[10px] ${hidden.length ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-500"}`} data-testid="dv3-col-settings-count">{shown}/{CONFIG_COLS.length}</span>
      </button>
      {open && createPortal(
        <div ref={panelRef} style={{ top: pos.top, right: pos.right }} className="fixed z-[90] w-56 bg-white rounded-lg border border-slate-200 shadow-xl p-2" data-testid="dv3-col-settings-panel">
          <p className="px-1.5 pb-1 text-[10px] text-slate-400">{T("勾选要显示的信息列（项目 / 交付物 固定显示）")}</p>
          {CONFIG_COLS.map(([k, l]) => {
            const on = !hidden.includes(k);
            return (
              <button key={k} type="button" onClick={() => onToggle(k)} data-testid={`dv3-col-toggle-${k}`} data-on={on ? 1 : 0}
                className="w-full flex items-center gap-2 px-1.5 py-1 rounded-lg hover:bg-slate-50 text-xs text-slate-700">
                <span className={`h-3.5 w-3.5 shrink-0 rounded border flex items-center justify-center ${on ? "bg-violet-600 border-violet-600 text-white" : "border-slate-300"}`}>{on && <Check className="h-3 w-3" />}</span>
                {T(l)}
              </button>
            );
          })}
          {hidden.length > 0 && <button type="button" onClick={onReset} data-testid="dv3-col-settings-reset" className="mt-1 w-full text-[11px] text-violet-700 hover:underline py-1 border-t border-slate-100">{T("恢复全部显示")}</button>}
        </div>, document.body)}
    </>
  );
};

/**
 * 表格视图表头（2026-10-16 加吸顶）：纵向滚动时整条表头冻结在卡片顶部。
 * 为什么 sticky 加在 th 上而不是 thead 上：th 才能同时承载「首列 left-0 横向吸附」，
 * 且 th 的 sticky 在 Safari 等浏览器上也稳；表头底色 / 下边框都放在 th 上，
 * 否则滚动时 tr 的边框留在原地、表头与首行之间会缺一条分隔线。
 * z 分层：普通表头格 z-[2] > 左侧冻结列（tbody td z-[1]）> 普通单元格；
 * 首列是左上角交叉格（同时吸附 top+left），必须压在所有格之上 → z-[3]。
 */
const TableHead = (props) => (
  <thead><tr className="text-left text-[10px] text-slate-400 bg-slate-50" data-testid="dv3-table-head">
    {/* hidden：列设置里被关掉的列（只可能是中间那组信息列，首列「项目」永远在，所以 i === 0 仍是项目、z-[3] 逻辑不受影响） */}
    {columnDefs(props).filter(({ key }) => !(props.hidden || []).includes(key)).map(({ key, className = "", plain, ...c }, i) => (
      /* plain 列（2026-10-27 起只有「标准结束日」）：只渲染列名，不挂 ThFilter 菜单 ——
         见 columnDefs 里该列的注释，给它筛选入口等于把 82 天重新变成业务判定口径。
         仍走同一份 className / z 分层，保证吸顶与冻结结构与其它表头一致。
         【2026-10-29】列定义可带 `title`（口径说明）：悬停给全文，并挂一个常驻 ⓘ，
         让人不必先猜「标准结束日」和「节点」是不是同一件事（IT 报告点名的误读）。 */
      plain
        ? <th key={key} data-testid={c.tid} data-plain="1" title={c.title || undefined} className={`py-2 px-3 whitespace-nowrap sticky top-0 bg-slate-50 border-b border-slate-200 ${i === 0 ? "z-[3]" : "z-[2]"} ${className}`}>
            <span className="inline-flex items-center gap-1">{c.label}{c.title && <Info className="h-3 w-3 text-slate-400" aria-hidden="true" />}</span>
          </th>
        : <ThFilter key={key} {...c} className={`sticky top-0 bg-slate-50 border-b border-slate-200 ${i === 0 ? "z-[3]" : "z-[2]"} ${className}`} />
    ))}
    {props.onDelete && <th className="py-2 px-3 w-10 sticky top-0 z-[2] bg-slate-50 border-b border-slate-200" />}
  </tr></thead>
);

/** 卡片 / 看板视图：同一套列筛选以 chip 形式呈现（plain 列无筛选语义，跳过） */
const QuickFilterBar = (props) => (
  <div className="flex flex-wrap items-center gap-1.5" data-testid="dv3-quick-filters">
    <span className="text-[11px] text-slate-400 mr-1">{T("快速筛选")}</span>
    {columnDefs(props).filter(({ plain }) => !plain).map(({ key, className, tid, ...c }) => <ThFilter key={key} as="chip" tid={tid.replace("dv3-th-", "dv3-qf-")} {...c} />)}
  </div>
);

/** 列表「耗时」：与详情页 HeadStrip 同一 signInfo（签署日起自然天数 · 不扣暂停 · ≤3 月绿 / 4–6 月橙 / >6 月红）；点击按同级筛选 */
const SignCell = ({ p, onPick }) => {
  const si = p.signInfo;
  if (!si) return <span className="text-[10px] text-slate-300" data-testid={`dv3-row-sign-${p.id}`} data-level="">—</span>;
  const txt = si.ended ? T("{0}（已移交）", [signElapsedText(si)]) : signElapsedText(si);
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onPick(si.paused ? "paused" : si.level); }} data-testid={`dv3-row-sign-${p.id}`} data-level={si.level} data-days={si.days} data-paused={si.paused ? 1 : 0}
      title={T("设计阶段已耗时 · 签署 {0}{1}{2} · 点击只看同级", [fmtD(si.date), si.pausedDays ? T(" · 含暂停 {0} 天", [si.pausedDays]) : "", si.paused ? T(" · 暂停中") : ""])}
      /* whitespace-nowrap（2026-10-23）：英文耗时最长会到「30 months 31 days」，
         不给 nowrap 时它会在这个 inline-flex 里折成两行 —— 实测把矩阵行高从 72px 顶高，
         同一行其他格子跟着变高、和上下行对不齐。列宽已按最坏情况放宽到 142px，
         nowrap 保证即使以后出现更长的值也只影响该格（td 有 overflow-hidden 兜底），
         不会再把行高搞乱；完整内容仍可从 title 读到。 */
      className={`inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-bold tabular-nums hover:underline ${si.paused ? "text-amber-600" : SIGN_NUM[si.level] || SIGN_NUM.green} ${si.level === "red" && !si.paused && !si.ended ? "animate-pulse" : ""}`}>
      {si.paused ? <PauseCircle className="h-3 w-3" /> : <Timer className="h-3 w-3" />}{txt}
    </button>
  );
};

const RoleTag = ({ k }) => <span className="text-[9px] font-bold tracking-wide text-slate-400 w-5 shrink-0">{k}</span>;

/**
 * 交付物芯片（项目表格「交付物」列专用，在历史 16 项全显方案上做增量）
 *
 * 修改原因（原始痛点）：
 *   原实现芯片里只写交付物名称，状态**完全靠底色**区分（产品原话：「现在只有颜色」）。
 *   色弱同事、小字号或投屏场景下读不出具体状态（完成 / 已批准 / 整改中 / 卡住 …），
 *   也就无法一眼看出「这个项目到底卡在哪一项交付物上」。产品给了交付物状态一览表的样例
 *   （Done / Approved / In Plan Check / Exempt / Working on it / 1st Correction / Stuck …），
 *   要求每个 step 都能直接读到状态文字。
 *
 * 最终想要的效果：
 *   芯片内容由「名称」变为「名称 · 状态文字」两段式；底色仍按状态着色（DS_COLOR），
 *   颜色与文字互为冗余 —— 习惯看色的看色、需要读字的读字，单看任一项都能判断状态。
 *   状态文字走 T() 翻译，前端语言切到英文时自动显示 Done / Approved / In Plan Check 等。
 *
 * 关键取舍：
 *   1) 仍按当前产物定义固定顺序全部渲染（含「未开始」灰色虚线占位），保证每行结构一致、可逐项横向比对；
 *      不采用卡片视图那套「只显示已启动 + 最多 8 个 + 「+N」」的截断策略 —— 表格是横向扫读场景，
 *      截断会丢信息，而卡片是单项目详情场景，两者取舍不同。
 *   2) 仍用 whitespace-nowrap 单行排布、不换行：换行会让同一表格各项目行高不齐、破坏逐行对齐。
 *   3) 名称用较淡字重（opacity-70）打底、状态文字加粗，形成两段式视觉，避免「名称状态」同色糊成一团；
 *      「未开始」这一档整体保持灰色虚线观感，只把状态字略提一档灰度（slate-500）保证可读。
 */
const DiscChip = ({ d, meta, tid, onClick }) => {
  const off = d.status === "not_started";
  const col = DS_COLOR[d.status] || DS_COLOR.not_started;
  const statusLabel = discStatusLabel(meta, d.status, d.discKind);
  const manual = d.discKind === "manual";
  return (
    <button type="button" data-testid={tid} data-status={d.status} data-kind={d.discKind || "design"} onClick={onClick} title={`${d.name}${d.zh ? ` ${d.zh}` : ""} · ${manual ? T("人工办理") : T("设计交付")} · ${statusLabel}`}
      className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap border transition-shadow hover:ring-1 hover:ring-slate-300 ${off ? "border-dashed border-slate-200 bg-slate-50 text-slate-400" : "border-transparent"}`}
      style={off ? undefined : { color: col.c, background: col.bg }}>
      {manual && <ClipboardCheck className="h-2.5 w-2.5 shrink-0" />}
      <span className={`font-medium ${off ? "" : "opacity-70"}`}>{pickL(d, "zh", "name")}</span>
      <span className={`font-bold ${off ? "text-slate-500" : ""}`}>{statusLabel}</span>
    </button>
  );
};

const TableView = ({ list, all, f, setF, setMany, owners, cnOwners, meta, onOpen, onOpenDisc, onDelete, hidden = [], presign = false, footer = null }) => (
  /* overflow-auto + card-fill：卡片吃掉页面根（.page-fill）的剩余高度、内部双轴自滚，
     表头（TableHead 的 th sticky top-0）与首列（td sticky left-0）因此都能真正冻结。
     minHeight 内联的原因同 MatrixView。
     2026-10-20 修正：ResponsiveTable 必须传 scrollOwner="ancestor"。该组件内部那层
     `.responsive-table-viewport` 原先自带 `overflow-x: auto`，浏览器会把 overflow-y 也算成 auto，
     于是它成了新的滚动容器 —— th 的 sticky top-0 吸附在这层上，这层自身没有纵向滚动量，
     表头就跟着卡片一起被卷走（实测卡片 scrollTop=400 时表头跑到视口外 -148px）。
     改由本卡片滚动、内层 overflow:visible 后，表头才真正贴住卡片顶部。 */
  <div className="bg-white rounded-lg border border-slate-200 overflow-auto card-fill responsive-table-scrollport" style={{ minHeight: 320 }} tabIndex={0} role="region" aria-label={T("设计项目表格，可横向滚动")} data-testid="dv3-table">
    <ResponsiveTable mobile="scroll" scrollOwner="ancestor" className="w-full text-xs">
      <TableHead all={all} f={f} setF={setF} setMany={setMany} owners={owners} cnOwners={cnOwners} meta={meta} onDelete={onDelete} hidden={hidden} />
      <tbody>{list.map((p) => (
        <tr key={p.id} data-testid={`dv3-row-${p.id}`} onClick={() => onOpen(p.id)} className="group border-b border-slate-100 last:border-0 hover:bg-violet-50/40 cursor-pointer">
          <td className="py-2 px-3 sticky left-0 z-[1] bg-white group-hover:bg-violet-50 min-w-[200px] sm:min-w-[260px] max-w-[320px] whitespace-nowrap shadow-[inset_-1px_0_0_#e2e8f0]" data-testid={`dv3-row-address-${p.id}`}><p className="font-semibold text-slate-800">{p.address}</p><p className="text-[10px] text-slate-400">{p.city} · {p.projectId}</p><BriefLine p={p} tid={`dv3-row-brief-${p.id}`} /><div className="mt-0.5 empty:hidden"><PresignChip p={p} presign={presign} tid={`dv3-row-presign-${p.id}`} /></div></td>
          {/* 中间信息列按「列设置」偏好逐个决定渲染 —— 表头（TableHead）用同一份 hidden 过滤，
              两边必须成对：只藏表头会错位，只藏单元格会多一列空表头。 */}
          {!hidden.includes("branch") && <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{T(meta.branches[p.branch]?.name)}</td>}
          {!hidden.includes("status") && <td className="py-2 px-3 whitespace-nowrap"><WsPill ws={p.workStatus} meta={meta} tid={`dv3-row-ws-${p.id}`} onClick={(e) => { e.stopPropagation(); setMany({ group: "", status: p.workStatus }); }} /></td>}
          {!hidden.includes("prog") && <td className="py-2 px-3 min-w-[150px]"><div className="flex items-center gap-2"><Progress v={p.progress} color={WS_COLOR[p.workStatus]?.c} /><b className="w-8 text-right">{formatProgress(p.progress)}</b></div></td>}
          {!hidden.includes("sign") && <td className="py-2 px-3 whitespace-nowrap"><SignCell p={p} onPick={(v) => setF("signLv", v)} /></td>}
          {!hidden.includes("owner") && <td className="py-2 px-3 text-slate-700 whitespace-nowrap">
            <div className="flex flex-col gap-0.5">
              <span className="inline-flex items-center gap-1"><RoleTag k="US" /><button type="button" data-testid={`dv3-row-owner-us-${p.id}`} title={T("美国建筑设计师 · 点击只看此负责人")} onClick={(e) => { e.stopPropagation(); setMany({ unassigned: "", owner: p.owner || T("未指派") }); }} className="hover:text-violet-700 hover:underline">{p.owner || "—"}</button><UnassignedChip p={p} tid={`dv3-row-unassigned-${p.id}`} /></span>
              <span className="inline-flex items-center gap-1"><RoleTag k="CN" /><button type="button" data-testid={`dv3-row-owner-cn-${p.id}`} title={T("中国建筑设计师 · 点击只看此负责人")} onClick={(e) => { e.stopPropagation(); setMany({ unassigned: "", cd: p.cdDesigner || "__none" }); }} className="hover:text-violet-700 hover:underline">{p.cdDesigner || "—"}</button></span>
            </div>
          </td>}
          {!hidden.includes("deadline") && <td className="py-2 px-3 whitespace-nowrap"><DeadlineBadge p={p} tid={`dv3-row-deadline-${p.id}`} /></td>}
          {/* 标准结束日（2026-10-27）：签约日 + 82 美国工作日，纯展示、超期标红。
              与上一列「节点」（阶段排期末端）刻意分开：这一列不随阶段自然顺延变化，
              只用来给管理层量「合同签了多久还没做完」，不参与任何判定。 */}
          {!hidden.includes("std") && <td className="py-2 px-3 whitespace-nowrap"><StdBaselineBadge p={p} tid={`dv3-row-std-${p.id}`} /></td>}
          <td className="py-2 px-3"><div className="flex flex-nowrap gap-0.5" data-testid={`dv3-row-discs-${p.id}`}>{(p.disciplines || []).map((x) => <DiscChip key={x.key} d={x} meta={meta} tid={`dv3-row-disc-${p.id}-${x.key}`} onClick={(e) => { e.stopPropagation(); onOpenDisc?.(p.id, x.key); }} />)}</div></td>
          {onDelete && <td className="py-2 px-3 text-right"><button type="button" data-testid={`dv3-row-delete-${p.id}`} title={T("删除项目（合同中途取消）")} onClick={(e) => { e.stopPropagation(); onDelete(p); }} className="text-slate-300 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button></td>}
        </tr>))}
        {footer && <tr data-testid="dv3-row-load-more"><td colSpan={99}>{footer}</td></tr>}
      </tbody>
    </ResponsiveTable>
  </div>
);

/**
 * 矩阵视图（2026-10-19 需求调整）。
 *
 * 列表布局继续沿用业务方已确认的矩阵样式：项目列、信息列、紧凑状态格和末尾负责人列不变；
 * 只把原来横向排列的“产物”替换为详情流程里的“阶段节点”。这样仍能纵向比较同一阶段在
 * 不同项目上的状态，又不会把阶段卡、子任务、产物清单重复塞进列表导致样式变形。
 * 点击阶段格进入阶段会话；会话右侧再展示该阶段挂载的产物，并与详情页共用阶段/评论数据。
 */
/* 矩阵格子里的阶段状态文案（2026-10-23 改为三态）。
   原来这里直接映射库里 12 个细粒度状态（含「就绪 / 已提交 / 等客户 / 需整改」），
   用户反馈「阶段状态不要因为内在产物的状态就变了，阶段就未开始 / 进行中 / 已完成」。
   映射规则（按流程前沿判定，不看 currentStage 指针）统一收在 meta.js 的 stagePhasesOf，
   这里只取三态文案，不再各自维护一份 12 态字典。
   ⚠️ 格子上的红框 / 超期天数 / 产物进度仍用原始 status 与 discipline* 字段计算，
   展示三态只影响「状态词」，不影响任何流程判定。 */
const stageToneOfPhase = (phase) => STAGE_PHASE_TONE[phase] || STAGE_PHASE_TONE.not_started;

/* 矩阵一行要画几个格子（当前实现）。

   表头列数始终等于当前阶段模板的可见阶段数，保证所有项目行共享同一套列边界。
   concurrent 行把 P+B 对应阶段合为 5 个节点，每个节点占 1 个标准列；这段区间内
   未被节点占用的列用一条 filler td 补齐，后面的 permit_handoff 与负责人列才不会左移。
   Building 图在新模板中是一个阶段，但顺序行仍聚合存量项目的 structural_t24 实例，
   因此同一格可以同时覆盖两条底层阶段记录。

   返回值：一行要渲染的格子数组，每项
     · key      代表 key（空列 = "__pb_gap__"；P+B 节点 = pb_xxx；其余 = 阶段 key）
     · members  覆盖的底层阶段 key
     · span     占几列（P+B 节点与独立阶段恒为 1，空列 = 组内剩余列数）
     · merged   是否 P+B 节点（true 时格子里多画一行「P+B + 节点名」）
     · filler   是否空列（只占位、不渲染内容）

   边界：permit-only / 设计-only 项目只实例化部分阶段。节点只在区间内至少有一个
   实际列时渲染；聚合成员可以包含历史阶段，但不能因此凭空增加表头列。 */
const PB_GAP_KEY = "__pb_gap__";
const stageCellsOf = (p, cols) => {
  const mode = (p.approvalMode || "sequential") === "concurrent" ? "concurrent" : "sequential";
  if (mode !== "concurrent") {
    return cols.map((c) => {
      const consolidation = stageConsolidationOf(c.key);
      return {
        key: c.key,
        members: consolidation?.keys || [c.key],
        span: 1,
        merged: false,
        consolidated: !!consolidation,
      };
    });
  }
  const pbSet = new Set(CONCURRENT_GROUPS.flatMap((g) => g.keys));
  const idx = cols.map((c, i) => (pbSet.has(c.key) ? i : -1)).filter((i) => i >= 0);
  /* 没有任何 P+B 阶段（permit-only 项目可能只建了其中几个，但至少会有一个）→ 退回原样 */
  if (!idx.length) return cols.map((c) => ({ key: c.key, members: [c.key], span: 1, merged: false }));
  /* 取「最长连续段」：全流程项目是 planning_set … building_approval；
     精简流程只取实际存在的连续段。 */
  let best = [idx[0], idx[0]];
  let cur = [idx[0], idx[0]];
  for (let k = 1; k < idx.length; k += 1) {
    if (idx[k] === idx[k - 1] + 1) cur[1] = idx[k];
    else cur = [idx[k], idx[k]];
    if (cur[1] - cur[0] > best[1] - best[0]) best = [...cur];
  }
  const [from, to] = best;
  const blockCols = cols.slice(from, to + 1);
  const present = new Set(blockCols.map((c) => c.key));
  const groups = CONCURRENT_GROUPS
    /* pb_set 的 members 保留 structural_t24：新项目没有这条实例，聚合时会自动跳过；
       存量项目有两条实例，必须同时计入状态、截止日和产物进度。 */
    .map((g) => ({ ...g, members: g.keys.slice() }))
    .filter((g) => g.members.some((k) => present.has(k)));
  const out = cols.slice(0, from).map((c) => ({ key: c.key, members: [c.key], span: 1, merged: false }));
  groups.forEach((g) => out.push({ key: g.key, members: g.members, span: 1, merged: true }));
  /* 空列 = 这段 P+B 区间的列数 - 实际画出的节点数。
     不渲染任何内容，只把行内列数补齐到与表头一致，让负责人列不跑位。 */
  const gap = blockCols.length - groups.length;
  if (gap > 0) out.push({ key: PB_GAP_KEY, members: [], span: gap, merged: false, filler: true });
  cols.slice(to + 1).forEach((c) => out.push({ key: c.key, members: [c.key], span: 1, merged: false }));
  return out;
};

/* 合并格的聚合状态：组内「全部完成」才算完成，「有一处受阻/需整改」就按最严重的显示。
   其余情况取组内第一个已启动的状态（组是同时推进的，取任何一个进行态都对，
   取第一个只是为了让显示稳定、不随数据顺序抖动）。
   截止日取**最晚**一个（组内两阶段可能不同步顺延，用最晚的才不会误报逾期）；
   产物进度求和（节点门禁就是按整组产物判的，分开算会让「还差几项」对不上）。

   【2026-10-23 三态位置化】phase 不再由本函数自己算，而是由调用方拿整行算好的
   `phases.nodePhaseOf` 传进来（原因见 meta.js stagePhasesOf 注释：当前阶段要看
   「前面收口了没有」，单看这一格算不出来，光看 currentStage 会跳）。 */
const aggregateStageCell = (p, members) => {
  const stages = members.map((k) => (p.stages || []).find((x) => x.key === k)).filter(Boolean);
  if (!stages.length) {
    return { status: "Not Started", current: false, dueDate: null, overdueDays: 0, overdueLevel: "", overdueFrom: "",
      dTotal: 0, dAll: 0, dSkip: 0, dExempt: 0, dApproved: 0, dPendingRv: 0, dStarted: 0, productsReady: true, missing: members.length };
  }
  const sts = stages.map((s) => s.status || "Not Started");
  const closed = ["Completed", "Approved", "Not Applicable"];
  const status = sts.every((s) => closed.includes(s)) ? "Completed"
    : sts.some((s) => s === "Blocked") ? "Blocked"
    : sts.some((s) => s === "Correction Required") ? "Correction Required"
    : sts.find((s) => ["Ready", "In Progress", "Internal Review", "Submitted", "Awaiting Client", "Awaiting External"].includes(s)) || "Not Started";
  /* 【2026-10-29】原来 `stages.map(s => s.dueDate).sort()` 是**字符串**排序，库里
     dueDate 同时存在纯日期 / 洛杉矶零点瞬间 / 历史建项时刻三种形态时，
     字典序最大 ≠ 日历日最大（详见 LegacyTabs.maxD 注释与 IT 报的「12/21 vs 12/22」）。
     改为归一成洛杉矶日历日后比较，回传原始值以保留形态给 fmtMD 使用。 */
  const dues = stages.map((s) => s.dueDate).filter(Boolean)
    .map((v) => ({ v, k: dateInputValue(v) || String(v).slice(0, 10) }))
    .sort((a, b) => (a.k < b.k ? -1 : a.k > b.k ? 1 : 0));
  const overDays = Math.max(0, ...stages.map((s) => Number(s.overdueDays || 0)));
  const levels = stages.map((s) => s.overdueLevel || "");
  /* 【2026-10-29】逾期来源标记：合并格「截止日取最晚、逾期取最严重」两个聚合可能来自
     不同成员（IT 报告 930 / 1090 / 340 / 364 都提了「P+B 显示未来日期却标红」）。
     这里把**逾期最严重那个成员**的 key 一起下发，让格子能写明「超 1 天（源自 P）」，
     读者就不会以为整组都逾期。单成员格不需要（它就是来源），返回空串。
     overdueFrom 只用于展示，不参与任何门禁 / 筛选判定。 */
  const worst = stages.reduce((a, s) => (Number(s.overdueDays || 0) > Number(a?.overdueDays || 0) ? s : a), null);
  return {
    status,
    dueDate: dues.length ? dues[dues.length - 1].v : null,
    overdueDays: overDays,
    overdueLevel: levels.includes("over") ? "over" : levels.includes("late") ? "late" : "",
    overdueFrom: stages.length > 1 && overDays > 0 ? (worst?.key || "") : "",
    dTotal: stages.reduce((a, s) => a + Number(s.disciplineTotal || 0), 0),
    /* 与详情页对齐用的分母：全部产物数 + 其中豁免数（缺字段的老数据退回 dTotal）。 */
    dAll: stages.reduce((a, s) => a + Number(s.disciplineTotalAll ?? s.disciplineTotal ?? 0), 0),
    dSkip: stages.reduce((a, s) => a + Number(s.disciplineSkipped || 0), 0),
    /* 豁免总数（含历史直接改状态的）：显示用「· 豁免 N」，与详情页同一个数。
       dSkip 只用于补分子 —— 被跳过的产物必然处于 exempt，说明它其实已经闭环。 */
    dExempt: stages.reduce((a, s) => a + Number(s.disciplineExempt ?? s.disciplineSkipped ?? 0), 0),
    dApproved: stages.reduce((a, s) => a + Number(s.disciplineApproved || 0), 0),
    dPendingRv: stages.reduce((a, s) => a + Number(s.disciplinePendingReview || 0), 0),
    dStarted: stages.reduce((a, s) => a + Number(s.disciplineStarted || 0), 0),
    productsReady: stages.every((s) => s.productsReady !== false),
    missing: members.length - stages.length,
  };
};

/**
 * 矩阵信息列单元格（2026-10-18）
 *
 * 需求：矩阵视图不仅要比对 13 项交付物，还要在每一行左侧直接看到项目的
 * 分公司、工作状态、进度、耗时、负责人和节点；用户同时要求这些列可配置。
 *
 * 实现取舍：
 *   1) 直接复用表格视图已有的 WsPill / SignCell / DeadlineBadge 等展示组件，
 *      不复制第二套状态文案与颜色，避免表格与矩阵对同一项目显示不同口径。
 *   2) 单元格点击仍保留表格里的「只看此值」筛选；通过 onFilter 回传到页面 URL 筛选，
 *      用户从矩阵也能完成从「看到异常」到「筛出同类项目」的闭环。
 *   3) 负责人的姓名来自项目行数据，不再请求接口；矩阵原有的「操作 · 项目负责人」
 *      是管理员可编辑的项目负责人列表，与这里展示的 US/CN 建筑设计师语义不同，因此不能合并。
 */
const MatrixInfoCell = ({ col, p, meta, onFilter }) => {
  const stop = (fn) => (e) => { e.stopPropagation(); fn(e); };
  if (col === "branch") {
    return <span className="text-[10px] font-medium text-slate-600 whitespace-nowrap">{T(meta.branches[p.branch]?.name) || "—"}</span>;
  }
  if (col === "status") {
    return <WsPill ws={p.workStatus} meta={meta} tid={`dv3-mx-ws-${p.id}`} onClick={stop(() => onFilter({ group: "", status: p.workStatus }))} />;
  }
  if (col === "prog") {
    return (
      <div className="flex w-full items-center gap-1.5">
        <Progress v={p.progress} color={WS_COLOR[p.workStatus]?.c} />
        <b className="w-7 shrink-0 text-right text-[10px] tabular-nums text-slate-700">{formatProgress(p.progress)}</b>
      </div>
    );
  }
  if (col === "sign") {
    return <SignCell p={p} onPick={(v) => onFilter({ signLv: v })} />;
  }
  if (col === "owner") {
    return (
      <div className="flex min-w-0 flex-col gap-0.5 text-[10px] leading-tight">
        <span className="inline-flex min-w-0 items-center gap-1">
          <RoleTag k="US" />
          <button type="button" data-testid={`dv3-mx-owner-us-${p.id}`} title={T("美国建筑设计师 · 点击只看此负责人")}
            onClick={stop(() => onFilter({ unassigned: "", owner: p.owner || T("未指派") }))}
            className="min-w-0 max-w-[82px] truncate font-medium text-slate-700 hover:text-violet-700 hover:underline">
            {p.owner || "—"}
          </button>
          <UnassignedChip p={p} tid={`dv3-mx-unassigned-${p.id}`} />
        </span>
        <span className="inline-flex min-w-0 items-center gap-1">
          <RoleTag k="CN" />
          <button type="button" data-testid={`dv3-mx-owner-cn-${p.id}`} title={T("中国建筑设计师 · 点击只看此负责人")}
            onClick={stop(() => onFilter({ unassigned: "", cd: p.cdDesigner || "__none" }))}
            className="min-w-0 max-w-[82px] truncate font-medium text-slate-700 hover:text-violet-700 hover:underline">
            {p.cdDesigner || "—"}
          </button>
        </span>
      </div>
    );
  }
  if (col === "deadline") {
    return <DeadlineBadge p={p} tid={`dv3-mx-deadline-${p.id}`} />;
  }
  if (col === "std") {
    /* 标准结束日：纯展示，**不挂 onFilter**。
       原因：这是一把「给管理层看的尺子」（签约日 + 82 美国工作日），不是业务时间源；
       一旦让它可点击筛选，使用习惯上很快会有人拿它当「逾期项目」的入口，
       等于把 82 天又悄悄回流成业务判定 —— 这正是 2026-10-27 这次改造要拆开的东西。
       阶段逾期请继续用「节点」列 / 「阶段任务逾期」筛选（都读 design_stages.dueDate）。 */
    return <StdBaselineBadge p={p} tid={`dv3-mx-std-${p.id}`} />;
  }
  return null;
};

export const MatrixView = ({ list, all, meta, api, onOpen, onOpenStage, onSaved, hidden = [], onFilter, unread = {}, onOpenComments, presign = false, footer = null }) => {
  // 2026-10-19：矩阵内容从产物改为阶段节点，但保留原矩阵的紧凑表格结构。
  // 阶段列取未筛选项目实际存在的阶段并按模板排序，保证筛选后列结构稳定；
  // 旧项目没有实例化阶段时才回退到模板，避免整个矩阵只显示项目列。
  /* 2026-10-19 用户需求：项目管理（签约后）不再显示「前期方案咨询 / 合同签署」两列；
     「签约前营销方案」页仍完整展示这两列，因此按 presign 决定是否过滤阶段模板。
     这里只过滤展示列，项目阶段数据与阶段会话不受影响。 */
  const stageTemplate = (meta.stageTemplate || []).filter((s) => !isStageHiddenInProjects(s.key, presign ? "pre_sign" : "signed"));
  // 统计“项目实际存在哪些阶段”时也只认可见阶段：若某项目只剩被隐藏的两步，
  // 回退到完整模板，避免矩阵只剩项目列、看不到任何阶段。
  const usedStageKeys = new Set(all.flatMap((p) => (p.stages || []).map((s) => canonicalStageKey(s.key))).filter((k) => stageTemplate.some((s) => s.key === k)));
  const cols = usedStageKeys.size ? stageTemplate.filter((s) => usedStageKeys.has(s.key)) : stageTemplate;
  /* 表头列只由当前模板决定，不随筛选结果或并发模式折列。
     sequential 行按阶段逐列；concurrent 行用 5 个单列 P+B 节点加 filler
     补齐。两种行都与同一份 cols 对齐，避免标题和表体横向错位。 */
  const headCells = cols.map((c) => ({ key: c.key, members: [c.key], span: 1, merged: false }));
  /* 【2026-10-22 第七轮·统一格宽】表格总宽显式算出来，不再用 w-max。

     为什么必须算：`w-max` 是 `width: max-content`，会先按**内容**测量一遍再定列宽。
     即使装了 table-layout: fixed、给每个 th 写了固定 width，Chrome 在这套组合下
     仍会把「Building 批+Permit」「Planning 批+二款」这类长标签算进 max-content，
     把那几列顶到 124/112/101px（改造前实测），于是同一行里格子宽窄不一。
     改成「左边固定列 + 信息列固定宽 + 15×MATRIX_STAGE_WIDTH + 右侧固定列」的精确总宽后，
     fixed 布局的分母确定，每列就是严格的 MATRIX_STAGE_WIDTH，色块（w-full）也就等宽了。 */
  const visibleInfoCols = CONFIG_COLS.filter(([k]) => !hidden.includes(k));
  const tableWidth = 260 + visibleInfoCols.reduce((a, [k]) => a + (MATRIX_INFO_WIDTH[k] || 96), 0)
    + cols.length * MATRIX_STAGE_WIDTH + 150;
  const [people, setPeople] = useState([]);
  // 2026-10-19 用户调整：负责人不再有「继承 / 完全独立」模式，弹窗直接给三个下拉，
  // 与项目详情「设置」页签同源（owner / cdDesigner / submissionOwner）。
  const [edit, setEdit] = useState(null);   // { p, owner, cdDesigner, submissionOwner }
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/design/disc-people").then(({ data }) => setPeople(data.people || [])).catch(() => {}); }, [api]);
  const nameOf = (un) => people.find((x) => x.username === un)?.name || un;
  const saveMgr = async () => {
    setBusy(true);
    try {
      const { p, owner, cdDesigner, submissionOwner } = edit;
      // owner / cdDesigner 存的是**显示名**（与详情设置页签的 Sel 同口径），
      // 交图员存 username —— 两个接口字段类型不同，必须分开写。
      const patch = {};
      if ((owner || "") !== (p.owner || "")) patch.owner = owner || "";
      if ((cdDesigner || "") !== (p.cdDesigner || "")) patch.cdDesigner = cdDesigner || "";
      if (Object.keys(patch).length) await api.patch(`/design/v3/projects/${p.id}`, patch);
      if ((submissionOwner || "") !== (p.submissionOwner || "")) {
        await api.put(`/design/v3/projects/${p.id}/managers`, { submissionOwner: submissionOwner || "" });
      }
      toast.success(T("负责人已保存"));
      setEdit(null); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const openManager = (p) => setEdit({
    p,
    owner: p.owner || "",
    cdDesigner: p.cdDesigner || "",
    submissionOwner: p.submissionOwner || "",
  });
  // 设计师下拉的候选：设计部成员（meta.designers，与详情设置页签同一份），
  // 再兜底当前值 —— 项目里可能存着已离职 / 不在名单里的人名，不能被下拉吃掉。
  const designerNames = useMemo(() => {
    const list = ((meta && meta.designers) || []).map((u) => u.name || u.username).filter(Boolean);
    return [...new Set(list)].sort((a, b) => a.localeCompare(b));
  }, [meta]);

  return (
    /* overflow-auto + card-fill（2026-10-16）：卡片吃掉页面根（.page-fill）剩余高度、内部双轴自滚。
       原先只有 overflow-x-auto —— 它把该盒子变成了滚动容器（overflow-x 非 visible 时 overflow-y
       的计算值也是 auto），却又没有纵向滚动量，于是 sticky 的 top 被"锁死"在这个盒子里、表头照样
       随页面滚走。限高后容器自己纵向能滚，表头吸顶才成立。
       minHeight 用内联样式（不写 Tailwind min-h-* 类）：.card-fill 自带 min-height:0（那是它能被
       压缩、从而"吃掉剩余高度"的前提），两个类同权重时谁赢取决于样式表顺序、不可靠；内联优先级最高，
       只把下限抬到 320px —— 视口太矮时宁可页面重新出现滚动条，也不要把表格压得只剩一两行。

       2026-10-20 修正（用户截图：「这里显示有问题」，第一行被表头压住半行）：
       根因同上（ResponsiveTable 内层 viewport 因 overflow-x:auto 连带产生 overflow-y:auto，
       成了第二层滚动容器）。矩阵的列头写的是 top-7（给上方 28px 图例行让位），
       sticky 参照物一旦变成那层内层容器，列头就被整体下推 28px、压住第一行；
       外层卡片纵向滚动时列头也不再吸顶（实测 scrollTop=300 后列头停在 47px 而不是卡片顶）。
       现在改由本卡片滚动（responsive-table-scrollport + tabIndex/role 接过原内层的键盘焦点与语义），
       内层传 scrollOwner="ancestor" 设为 overflow:visible，top-0 / top-7 的参照物回到本卡片，
       图例行与列头两段式吸顶恢复正常，第一行也不会再被压住。 */
    <div className="bg-white rounded-lg border border-slate-200 overflow-auto card-fill responsive-table-scrollport" style={{ minHeight: 320 }} tabIndex={0} role="region" aria-label={T("设计项目矩阵，可横向滚动")} data-testid="dv3-matrix">
      {/* 图例行也吸顶（2026-10-20 用户反馈「这一行需要冻结」）：
          原先这里只有 sticky left-0 —— 横向滚动时图例跟着走，但纵向滚动时它照样被卷走，
          用户往下翻项目时看不到「阶段节点 / 阶段会话」这组图例，也不知道灰绿配色代表什么。
          现在补上 top-0 与列头组成两段式吸顶：图例固定 28px（h-7，含 1px 下边框），
          列头用 top-7 紧贴其下（见下方各 th），合起来就是用户圈出的「图例 + 列名」整条。
          高度写死 h-7 而不是靠 py 撑：列头的 top 偏移是固定值，图例行高必须同样确定，
          否则字号/行高微调后两段之间会露缝或重叠。z-[4] 高于列头（z-[2]/z-[3]），
          保证表头下面滚上来的行不会盖住图例；bg-white 必须不透明，否则行内容会透出来。 */}
      <div className="sticky top-0 left-0 z-[4] flex h-7 items-center gap-3 whitespace-nowrap border-b border-slate-100 bg-white px-3 text-[10px] text-slate-500">
        <span className="inline-flex items-center gap-1"><Layers className="h-3 w-3 text-violet-600" />{T("阶段节点")}</span>
        <span className="inline-flex items-center gap-1"><MessageSquareText className="h-3 w-3 text-teal-600" />{T("阶段会话")}</span>
      </div>
      {/* 表格宽度由 tableWidth 精确给定（见上），不再用 w-max —— 原因见那段注释。 */}
      <ResponsiveTable mobile="scroll" scrollOwner="ancestor" style={{ width: tableWidth, minWidth: tableWidth }} className="table-fixed text-xs">
        <thead>
          <tr className="bg-slate-50">
            {/* 左上角交叉格：同时吸附 top + left，z 最高；底色 / 下边框必须写在 th 上（写在 tr 上滚动时不动）。
                【合并 2026-10-16】main 侧引入了「表头吸顶冻结」（top-0 + border-b + z 分层），
                zx-dev 侧引入了「操作 · 项目负责人」列（右上角 sticky right-0，tbody 已有对应 td）。
                二者叠加：三列都吸顶，左上 / 右上两个交叉格 z 取最高。
                【2026-10-20】上方图例行改为 top-0 吸顶后，列头必须让位到 top-7（图例行 h-7），
                否则两段会叠在同一高度、图例被盖住。 */}
            <th className="sticky top-7 left-0 z-[3] w-[260px] bg-slate-50 text-left py-2 px-3 text-[10px] font-bold text-slate-500 whitespace-nowrap border-b border-slate-200 shadow-[inset_-1px_0_0_#e2e8f0] max-sm:static">{T("项目")}</th>
            {/* 信息列与表格共用 hidden 偏好。项目列固定在左侧、阶段列固定在其后，
                所以这里只过滤信息列；隐藏后表头和 tbody 使用同一数组，列数始终对齐。
                【2026-10-29 补矩阵列头 ⓘ】此前只有**表格视图**的列头带口径说明
                （TableHead 的 plain 分支 + ThFilter 的 title），切到矩阵视图后
                「节点 / 标准结束日」两个列名是裸的 —— IT 报告点名的那两个误读
                （标准结束日超期 810 天当成阶段延误、节点与阶段预计混为一谈）
                在矩阵视图里依旧没有任何解释入口。用户 2026-10-29 明确要求「给矩阵加上」。
                文案取 `COL_TITLE`（与表格视图同一份常量），不另写一套措辞。
                列宽不动：ⓘ 只有 12px，而这两列的列宽是按**单元格内容**（英文最坏
                「Overdue 809 days · 08/09/2024」≈185px）定的，列名本身远短于内容，
                加图标不会挤压；其余信息列无 title，行为一字不变。 */}
            {CONFIG_COLS.filter(([k]) => !hidden.includes(k)).map(([k, l]) => <MatrixInfoTh key={k} colKey={k} label={l} />)}
            {/* 阶段列头与当前模板同序。结构/T24 已并入 Building 图，不再单独成列。
                表体里 concurrent 行使用固定 P+B 节点和 filler，列总数仍与表头一致。
                节点的名字（P+B 图纸 / P+B 材料 / …）写在格子里而不是表头，
                因为表头是所有行共享的：一旦在表头上标 P+B，那 100 个 sequential 行
                的「Planning 图 / Building 图」等列名就会消失或错位。 */}
            {headCells.map((h) => {
              const headerName = T(STAGE_SHORT[h.key] || h.name || h.key);
              const headerNote = h.key === "building_arch" ? T(STAGE_SHORT_NOTE.building_arch) : "";
              return (
              /* 【2026-10-22 第七轮·统一格宽】所有阶段列固定同一宽度，不再按内容 auto 撑开。
                 用户原话：「不去对应表头了，还是按照以前那样，每一列就是一个矩阵格子，
                 然后在 P+B 模式下依次往后排就是了。。现在会造成矩阵格子宽度不统一」。
                 改造前每列只有 min-w-[96px] 且表头 whitespace-nowrap，长标签
                 （Building 批+Permit 等）会把该列顶到 101/112/124px，于是同一行里
                 有的格子 96、有的 124 —— 这就是用户说的「格子宽度不统一」的列侧来源。
                 现在三件一起做，保证格子是等宽矩形：
                   ① 列宽写死 MATRIX_STAGE_WIDTH（width 而不是 min-width，table-fixed 下即最终列宽）；
                   ② 表头 overflow-hidden + text-ellipsis，超长的标签截断而不是把列顶宽，
                      完整名称放在 title 里（悬停可见），英文界面标签更长时同样不会破版；
                   ③ 表体按钮加 w-full（见下方 drawCell），色块撑满整格，不再按文字宽度收缩。
                 列数守恒：P+B 行只占用节点格，剩余位置由 filler 补齐，右侧负责人列不会被左拉。 */
              <th key={h.key} data-testid={`dv3-mx-th-${h.key}`} data-kind="stage" colSpan={h.span}
                data-consolidated={headerNote ? "1" : undefined}
                style={{ width: MATRIX_STAGE_WIDTH, minWidth: MATRIX_STAGE_WIDTH, maxWidth: MATRIX_STAGE_WIDTH }}
                title={headerNote ? `${headerName} · ${headerNote}` : headerName}
                className="sticky top-7 z-[2] overflow-hidden px-1 py-2 text-[10px] font-bold border-b border-slate-200 bg-slate-50 text-slate-500">
                {/* 名称与详情阶段条同源（STAGE_SHORT）：模板全名如「现场测量 + 811/Utility」太长，
                    会把矩阵列宽和行高撑开，也和用户看到的详情阶段名对不上。
                    Building 图额外用第二行写「含结构 / T24」，避免长标签被截后看不出合并范围。 */}
                <span className="block truncate whitespace-nowrap">{headerName}</span>
                {headerNote && <span className="mt-0.5 block truncate whitespace-nowrap text-[9px] font-medium text-slate-400">{headerNote}</span>}
              </th>
              );
            })}
            <th className="sticky top-7 right-0 z-[3] bg-slate-50 py-2 px-2 min-w-[150px] text-left text-[10px] font-bold text-slate-500 whitespace-nowrap border-b border-slate-200 shadow-[inset_1px_0_0_#e2e8f0] max-sm:static" data-testid="dv3-mx-th-mgr">{T("负责人（US / CN / 交图）")}</th>
          </tr>
        </thead>
        <tbody>
          {list.map((p) => {
            /* 【2026-10-23 三态位置化】每行先把整条流程算一遍，再去画格子。
               必须在这里算，不能在单个格子里算：三态判定要「前面阶段收口了没有」，
               一格看不到整行。口径见 meta.js 的 stagePhasesOf 注释
               （用户原话：「流程进度不能跳..但是产物可以提前提交..提交后流程状态不会被影响」）。
               rowCells 只算一次，循环内外复用，避免 stageCellsOf 被调两遍。 */
            const rowCells = stageCellsOf(p, cols);
            const { phaseOf: rowPhaseOf, nodePhaseOf: rowNodePhaseOf, frontierKey: rowFrontier } = stagePhasesOf(
              rowCells.map((c) => ({
                key: c.key,
                members: c.members
                  .map((k) => (p.stages || []).find((x) => x.key === k))
                  .filter(Boolean)
                  /* 带上 City 批准（正式登记或后端推导）：矩阵三态据此把批准节点显示成「进行中」，
                     与详情页同一个判定 —— 否则又会出现「详情已批准、矩阵未开始」。 */
                  .map((s) => ({ key: s.key, status: s.status || "Not Started", actualFinish: s.actualFinish,
                                 cityApproval: s.cityApproval || s.cityApprovalDerived })),
              })),
            );
            return (
            <tr key={p.id} data-testid={`dv3-mx-row-${p.id}`} className="border-t border-slate-100 hover:bg-violet-50/40">
              <td onClick={() => onOpen(p.id)} className="sticky left-0 z-[1] w-[260px] bg-white hover:bg-violet-50 cursor-pointer py-2 px-3 shadow-[inset_-1px_0_0_#e2e8f0] max-sm:static" data-testid={`dv3-mx-addr-${p.id}`}>
                <div className="flex items-center gap-1.5 min-w-0">
                  <p className="font-semibold text-slate-800 truncate max-w-[300px]">{p.address}</p>
                  <CommentBadge n={unread[p.id]} tid={`dv3-mx-comments-${p.id}`} onClick={onOpenComments ? () => onOpenComments(p.id) : undefined} />
                </div>
                <p className="text-[10px] text-slate-400">{p.city} · {p.projectId}</p>
                <div className="mt-0.5 empty:hidden"><PresignChip p={p} presign={presign} tid={`dv3-mx-presign-${p.id}`} /></div>
              </td>
              {CONFIG_COLS.filter(([k]) => !hidden.includes(k)).map(([k]) => (
                /* overflow-hidden 是兜底（2026-10-23）：列宽已按英文实测宽度放大，本语言下不再溢出；
                   但状态名可被管理员在「产物配置」里自定义、以后还可能接西班牙语，文案长度没有上限。
                   table-fixed 下 td 宽度写死，内容若还超长，不裁就会画到右侧相邻列上（本次问题的形态）。
                   宁可裁掉也不越界：矩阵靠横向滚动看更多列，一格糊两列是完全读不了的。 */
                <td key={k} style={{ width: MATRIX_INFO_WIDTH[k] }} className="overflow-hidden px-1.5 py-2 align-middle" data-testid={`dv3-mx-info-td-${p.id}-${k}`}>
                  <MatrixInfoCell col={k} p={p} meta={meta} onFilter={onFilter} />
                </td>
              ))}
              {rowCells.map((cell) => {
                /* 2026-10-19 用户需求「列表按照阶段显示，然后在每个阶段里面控制产物」：
                   阶段格除了阶段状态，还要能看出这个阶段的产物交完没有、卡在哪。
                   · 有挂载产物时显示「已审核通过 / 需提交」的进度；
                   · 产物没全部通过审核 → 阶段格标琥珀色边框（提示还不能关阶段）；
                   · 阶段超截止日 ≥ R20 阈值（默认 2 天）→ 红色边框 + 「超 N 天」，
                     与通知设计部经理的口径一致。
                   2026-10-19 反馈修正（用户：「有产物未开始的可以不用黄框标注」）：
                   新建项目里产物默认全是「未开始」，之前按「未全部审核通过」一律标黄，
                   整个矩阵像大面积告警。现在只有阶段内**至少一件产物真正动过**
                   （disciplineStarted > 0）才标黄；全部未开始视为「还没启动」，不加框。
                   2026-10-19 二次修正（用户第二次反馈「为什么还有这种未开始的显示黄框」）：
                   只判「产物动过」仍不够 —— 阶段本身还停在「未开始 / 就绪」时，
                   哪怕有产物被手动改成 working，格子上写的仍是「未开始」，
                   此时再套黄框就自相矛盾。因此把**阶段自身是否已进入进行态**也纳入判断：
                   阶段状态仍在 Not Started / Ready 一律不标黄，只在阶段已启动
                   （In Progress / Internal Review / Submitted / 等外部 / 需整改 …）
                   且产物没交齐时才提示。
                   2026-10-19 三次修正（用户：「那这种情况其实黄框是可以理解的」）：
                   上一版把「阶段未开始」一刀切排除，误伤了真正需要提醒的状态不一致 ——
                   **产物已经在做、阶段却还停在「未开始」**，说明实际工作已启动而阶段状态没推进，
                   这种格子应当保留黄框提示。故黄框最终口径为：
                   阶段未关闭 + 有挂载产物 + 至少一件产物动过 + 产物未全部审核通过；
                   只有「阶段未开始且产物也全没动」这种纯新建/未开工的格子才不标黄。
                   高度统一（用户：「统一这些框的高度」）：格子固定为「状态 + 截止 + 产物」
                   三行，缺内容时渲染不可见占位而不是整行删掉，避免 43px / 61px 参差；
                   逾期天数并入截止行显示，不再额外撑出第四行。 */

                /* 【2026-10-22 P+B 合并·第五轮】单个节点格的绘制。抽成函数是因为一个 td 里
                   要画多个节点（P+B 大格内 5 个节点），两者必须长得一模一样 —— 状态行 /
                   截止行 / 产物行三行固定高度、配色同一套 stageTone、黄框同一套判定。
                   参数全部由调用方算好（已按组聚合或按单阶段），这里只负责画。

                   merged=true 时多画一行 P+B 名称（用户要求「名称位置标注清楚是 p+b 模式」）：
                   表头仍是 15 个底层阶段名，所以合并节点必须自己写明它是哪个节点，
                   否则用户只看到「已完成 / 进行中」却不知道这是 P+B 图纸还是 P+B 材料。 */
                /* phase 是显示三态（未开始 / 进行中 / 已完成），由调用方按**整行**算好传进来：
                   只有流程前沿那一格能是「进行中」，后面的格子哪怕原始状态是 In Progress
                   也显示未开始（用户口径：流程进度不能跳，产物可以提前提交但不影响阶段状态）。
                   合并节点取 rowNodePhaseOf（组内含前沿即进行中，全组收口才算完成）。 */
                /* ccur 现在指「本格是流程前沿」，不再是「project.currentStage 指向本格」。
                   原因：指针会因一次通过 / 补关历史阶段跳到还没走到的格子上，
                   跟着它标「当前」就会出现「未开始 · 当前」这种自相矛盾的格子
                   （2026-10-23 用户截图指出的正是这个）。 */
                const drawCell = ({ cellKey, members, st: cst, phase: cPhase, current: ccur, due, overD, overLv, overFrom = "", dTot, dAll, dSkip = 0, dExempt = 0, dApp,
                                    dPend, dStart, ready, merged, consolidated = false }) => {
                  const baseLabel = T(STAGE_PHASE_LABEL[cPhase] || STAGE_PHASE_LABEL.not_started);
                  const cLabel = consolidated && STAGE_SHORT_NOTE[cellKey]
                    ? `${baseLabel} · ${T(STAGE_SHORT_NOTE[cellKey])}`
                    : baseLabel;
                  const cTone = stageToneOfPhase(cPhase);
                  /* cClosed 直接由显示三态推出来，保证「徽章写已完成 ↔ 不再标黄框」永远同步。
                     等价性：处于 completed 态的就是 Completed / Approved / Not Applicable 三种。 */
                  const cClosed = cPhase === STAGE_PHASE.completed;
                  const cRing = overLv === "over" ? "ring-1 ring-red-400"
                    : (!cClosed && !ready && dTot > 0 && dStart > 0 ? "ring-1 ring-amber-300" : "");
                  const cHasDue = !!(ccur || due);
                  /* 合并节点的显示名：用 PB 节点的短名（STAGE_SHORT.pb_xxx =「P+B 图纸」），
                     但要把名称自带的「P+B」前缀去掉 —— 左侧已经有 P+B 徽标，不去重会显示成
                     「P+B P+B 图纸」。前缀在**翻译之后**再去：中文是「P+B 图纸」、英文是
                     「P+B Drawings」，两种语言都以前缀开头，用同一个正则即可同时兼容。 */
                  const cFullName = merged ? T(STAGE_SHORT[cellKey] || cellKey) : "";
                  const cName = cFullName.replace(/^P\+B\s*/i, "");
                  return (
                    <button type="button" key={cellKey} data-testid={`dv3-mx-cell-${p.id}-${cellKey}`} data-status={cst} data-kind="stage"
                      /* data-phase 是显示三态的新锚点；data-status 保留原始 12 态，
                         既有 E2E（按 data-status="Not Started" 数黄框）与调试都还能用。 */
                      data-phase={cPhase}
                      data-products-ready={ready ? "1" : "0"} data-overdue-level={overLv}
                      data-products-started={dStart}
                      {...(merged ? { "data-merged": "1", "data-members": members.join(",") } : {})}
                      {...(consolidated ? { "data-consolidated": "1", "data-members": members.join(",") } : {})}
                      onClick={() => onOpenStage?.(p.id, cellKey)}
                      style={ink(cTone.c, cTone.bg)}
                      title={[
                        `${p.address} · ${T(STAGE_SHORT[cellKey] || cellKey)} · ${cLabel}`,
                        (merged || members.length > 1) ? T("覆盖阶段：{0}", [members.map((k) => T(STAGE_SHORT[k] || k)).join(" + ")]) : "",
                        due ? T("截止 {0}", [fmtD(due)]) : "",
                        /* 【2026-11-04】口径修正：这一格统计的是「已闭环」的产物数（approved / done / exempt 都算，
                   后端 DISC_TERMINAL_STATUSES），而豁免的产物并没有"审核通过"这一步。
                   继续写「已审核通过」会让"豁免 3 份"的项目看起来像"审过 3 份"。改叫「已就绪」。 */
                        dTot > 0 ? T("产物 {0}/{1} 已就绪{2}{3}", [dApp + dSkip, dAll ?? dTot, dExempt > 0 ? T(" · 豁免 {0}", [dExempt]) : "", dPend ? T(" · 待审核 {0}", [dPend]) : ""]) : "",
                        overD > 0 ? T("已超期 {0} 天", [overD]) : "",
                        T("点击进入阶段会话"),
                      ].filter(Boolean).join(" · ")}
                          /* 【2026-10-22 第七轮·统一格高】min-h-[72px] 把所有格子锁成同一高度。
                         原因：P+B 节点比普通阶段多一行「P+B + 节点名」（12px + 2px 间距），
                         实测内容高度 P+B 71px vs 普通 68px，同一行里高低不齐（用户截图指出）。
                         取 72 而不是 71：留 1px 余量，避免不同语言（英文标签更高）或
                         字体缩放时 P+B 格又被内容顶过 min-height 而再次不齐。
                         普通阶段内容少，靠 justify-center 在格子内垂直居中。 */
                      className={`flex w-full min-w-0 min-h-[72px] flex-col justify-center overflow-hidden rounded border border-transparent px-1 py-1 text-center text-[10px] font-semibold whitespace-nowrap transition-shadow hover:ring-1 hover:ring-slate-300 ${cRing}`}>
                      {/* 合并节点自己带 P+B 徽标：表头是底层阶段名，不能指望它表达「这是节点」。
                          徽标与名称同一行、名称 flex-1 居中，保证 5 个节点视觉对齐。 */}
                      {merged && (
                        <span className="mb-0.5 flex w-full min-w-0 items-center justify-center gap-0.5 overflow-hidden leading-[12px]"
                          data-testid={`dv3-mx-cell-label-${p.id}-${cellKey}`}>
                          <span className="shrink-0 rounded bg-teal-600 px-0.5 text-[8px] font-bold leading-[13px] text-white">P+B</span>
                          <span className="min-w-0 truncate text-[9px] font-bold text-slate-700" title={cName}>{cName}</span>
                        </span>
                      )}
                      <span className="block w-full min-w-0 truncate leading-[14px]">{cLabel}</span>
                      {/* 【2026-10-22 第七轮·防溢出】列宽固定成 112px 之后，这一行的内容
                          （「当前」+ 截止日 + 逾期天数）在英文界面会超宽（实测最多溢出 17px），
                          文字直接压到右边相邻格子上。这里三处一起收口：
                          ① 本行 overflow-hidden + min-w-0，超出部分不许画出格子；
                          ② 「当前」与日期 shrink-0 —— 它们短且是判断依据，优先保留；
                          ③ 逾期文案 min-w-0 truncate —— 它最长且可推断（格子上还有红/黄描边
                             和 title 里的完整说明），放不下就省略号，不再是压到隔壁。 */}
                      <span className={`mt-0.5 flex w-full min-w-0 items-center justify-center gap-0.5 overflow-hidden text-[9px] font-bold leading-none tabular-nums text-slate-600 ${cHasDue || overD > 0 ? "" : "invisible"}`}
                        data-testid={`dv3-mx-stage-due-${p.id}-${cellKey}`} data-current={ccur ? "1" : "0"} data-due={due || ""}>
                        {ccur && <span className="shrink-0 text-violet-700">{T("当前")}</span>}
                        {/* 【2026-10-29 时区统一】原为 `String(due).slice(5,10)`，取的是 UTC 的月-日，
                            历史截止日（建项时刻写入，如 10-30T00:09Z）在洛杉矶其实是 10-29，
                            格子里就会比阶段卡早/晚一天。改走 fmtMD 按洛杉矶换算。 */}
                        {due && <span className="inline-flex shrink-0 items-center gap-0.5"><Timer className="h-2.5 w-2.5" aria-hidden="true" />{fmtMD(due)}</span>}
                        {overD > 0 && (
                          <span className={`min-w-0 truncate ${overLv === "over" ? "text-red-700" : "text-amber-700"}`}
                            data-testid={`dv3-mx-stage-overdue-${p.id}-${cellKey}`} data-overdue-from={overFrom || ""}
                            title={overFrom ? T("逾期来自组内成员：{0}。本格日期显示组内最晚截止日（整套图交齐时间），两者不是同一个日期。", [STAGE_SHORT[overFrom] ? T(STAGE_SHORT[overFrom]) : overFrom]) : undefined}>
                            {overLv === "over" ? T("超 {0} 天", [overD]) : T("逾期 {0} 天", [overD])}
                          </span>
                        )}
                      </span>
                      {/* 产物行同样防溢出：固定列宽下「0/8 待审 3」在窄语言/多位数字时会超宽。 */}
                      {/* 【2026-11-04 两边对齐】分母用**本阶段全部产物**（含登记为无需提交的），
                          与详情页「交付物 7/8」完全同一个数；被豁免的单独写「· 豁免 N」。
                          之前矩阵分母剔掉了豁免（6/7），详情却是 7/8，同一个阶段两种说法。
                          ✅ 推进判定（ready / 黄框）仍用 dTot（需提交数），不受显示口径影响。 */}
                      <span className={`mt-0.5 flex h-[14px] w-full min-w-0 items-center justify-center gap-0.5 overflow-hidden text-[9px] font-bold leading-none tabular-nums ${(dAll ?? dTot) > 0 ? (ready ? "text-emerald-700" : (!cClosed && dStart > 0 ? "text-amber-700" : "text-slate-400")) : "invisible"}`}
                        data-testid={`dv3-mx-stage-products-${p.id}-${cellKey}`} data-approved={dApp} data-total={dAll ?? dTot}
                        data-required={dTot} data-skipped={dSkip}>
                        <Package className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />{/* 分子 = 已闭环数：dApp 统计的是「需提交」的那批，被跳过（必然 exempt）的补回来，
                            这样与详情页的 doneN（全部产物里闭环的数量）完全一致。 */}
                        <span className="shrink-0">{dApp + dSkip}/{dAll ?? dTot}</span>
                        {dExempt > 0 && <span className="min-w-0 truncate text-slate-500">{T("豁免 {0}", [dExempt])}</span>}
                        {dPend > 0 && <span className="min-w-0 truncate text-amber-800">{T("待审 {0}", [dPend])}</span>}
                      </span>
                      {/* 兼容锚点：改造前每个底层阶段都有自己的 cell testid，
                          E2E / 自动化脚本按 `dv3-mx-cell-{pid}-building_arch` 定位。
                          合并后这些阶段没有独立 td 了，但仍保留同名锚点（点击冒泡到按钮），
                          避免既有脚本因为「节点合并」而整批失效。 */}
                      {(merged || consolidated) && members.filter((m) => m !== cellKey).map((m) => (
                        <span key={m} data-testid={`dv3-mx-cell-${p.id}-${m}`} data-member-of={cellKey} className="hidden" aria-hidden="true" />
                      ))}
                    </button>
                  );
                };

                /* 空列占位：concurrent 行在 P+B 区间内只画 5 个节点、每格 1 列，
                   剩余列留空。这一条 td 不渲染内容，只为把行内总列数补齐到表头列数，
                   让后面的 permit_handoff 与负责人列仍待在自己表头下面。
                   为什么需要它：<tr> 的单元格数量少于表头时，表格会把右侧单元格整体左拉，
                   permit_handoff 就会跑到前面的阶段列下面。 */
                if (cell.filler) {
                  return (
                    <td key={cell.key} colSpan={cell.span} className="bg-slate-50/40"
                      data-testid={`dv3-mx-td-${p.id}-${cell.key}`} data-span={cell.span} aria-hidden="true" />
                  );
                }

                // 矩阵列是所有项目阶段的并集；未建立的阶段不是「未开始」。
                if (!cell.members.some((key) => (p.stages || []).some((stage) => stage.key === key))) {
                  const missingLabel = (p.stages || []).length ? T("此项目未设置该阶段") : T("暂无阶段进度");
                  return (
                    <td key={cell.key} colSpan={cell.span} className="p-0.5 text-center text-slate-400" data-testid={`dv3-mx-td-${p.id}-${cell.key}`}>
                      <span data-testid={`dv3-mx-cell-${p.id}-${cell.key}`} title={missingLabel} aria-label={missingLabel}>—</span>
                    </td>
                  );
                }

                /* 【2026-10-23 固定合并】顺序流程里的 Building 图一格同时覆盖
                   building_arch 与存量项目的 structural_t24。表头只保留一个
                   Building 图，不再为结构/T24 多画一列。 */
                if (cell.consolidated) {
                  const a = aggregateStageCell(p, cell.members);
                  return (
                    <td key={cell.key} className="p-0.5" data-testid={`dv3-mx-td-${p.id}-${cell.key}`}
                      data-consolidated="1" data-members={cell.members.join(",")}>
                      {drawCell({
                        cellKey: cell.key, members: cell.members, st: a.status,
                        phase: rowNodePhaseOf[cell.key] || STAGE_PHASE.not_started,
                        current: cell.members.includes(rowFrontier),
                        due: a.dueDate, overD: a.overdueDays, overLv: a.overdueLevel, overFrom: a.overdueFrom,
                        dTot: a.dTotal, dAll: a.dAll, dSkip: a.dSkip, dExempt: a.dExempt, dApp: a.dApproved, dPend: a.dPendingRv, dStart: a.dStarted,
                        ready: a.productsReady, merged: false, consolidated: true,
                      })}
                    </td>
                  );
                }

                /* P+B 节点：每个节点自成一格、各占 1 列。
                   这里不再使用「一个大格里面塞 5 个小节点」的 flex 布局，
                   5 个节点就是 5 个独立的 td，宽度与 sequential 行的阶段格完全一致。
                   表格自己负责列宽，节点宽度自然与上方表头列对齐（虽然表头写的是底层阶段名，
                   但节点格自己带「P+B + 名称」，读起来不会混）。 */
                if (cell.merged) {
                  const a = aggregateStageCell(p, cell.members);
                  return (
                    <td key={cell.key} className="p-0.5" data-testid={`dv3-mx-td-${p.id}-${cell.key}`}
                      data-merged="1" data-members={cell.members.join(",")}>
                      {drawCell({
                        cellKey: cell.key, members: cell.members, st: a.status,
                        /* 合并节点的三态取整行算好的 nodePhaseOf（含前沿判定），
                           不再用组内成员状态自行组合 —— 否则「前面没做完、本组却已
                           Ready」时会谎报进行中，正是用户指出的跳格。 */
                        phase: rowNodePhaseOf[cell.key] || STAGE_PHASE.not_started,
                        /* 「当前」标在前沿所在的合并节点上（前沿=第一个未收口阶段，
                           新项目就是第一格，用户口径「第一个格子默认应该是进行中」）。 */
                        current: cell.members.includes(rowFrontier),
                        due: a.dueDate, overD: a.overdueDays, overLv: a.overdueLevel, overFrom: a.overdueFrom,
                        dTot: a.dTotal, dAll: a.dAll, dSkip: a.dSkip, dExempt: a.dExempt, dApp: a.dApproved, dPend: a.dPendingRv, dStart: a.dStarted,
                        ready: a.productsReady, merged: true,
                      })}
                    </td>
                  );
                }

                const stage = (p.stages || []).find((x) => x.key === cell.key);
                const st = stage?.status || "Not Started";
                return (
                  <td key={cell.key} className="p-0.5" colSpan={cell.span} data-testid={`dv3-mx-td-${p.id}-${cell.key}`}>
                    {drawCell({
                      cellKey: cell.key, members: cell.members, st,
                      phase: rowPhaseOf[cell.key] || STAGE_PHASE.not_started,
                      current: cell.key === rowFrontier,
                      due: stage?.dueDate, overD: Number(stage?.overdueDays || 0), overLv: stage?.overdueLevel || "",
                      dTot: Number(stage?.disciplineTotal || 0),
                      dAll: Number(stage?.disciplineTotalAll ?? stage?.disciplineTotal ?? 0), dSkip: Number(stage?.disciplineSkipped || 0),
                      dExempt: Number(stage?.disciplineExempt ?? stage?.disciplineSkipped ?? 0),
                      dApp: Number(stage?.disciplineApproved || 0),
                      dPend: Number(stage?.disciplinePendingReview || 0), dStart: Number(stage?.disciplineStarted || 0),
                      ready: stage?.productsReady !== false, merged: false,
                    })}
                  </td>
                );
              })}
              <td className="sticky right-0 z-[1] bg-white p-1 max-sm:static" data-testid={`dv3-mx-mgr-${p.id}`}>
                {/* 负责人列直接读三个源字段（owner / cdDesigner / submissionOwner），
                    与弹窗、详情「设置」页签同源；不再绕 effectiveManagers 派生视图。 */}
                <button type="button" disabled={!meta?.isManager || (presign && p.presignInfo?.recordOnly)} onClick={() => openManager(p)}
                  className="w-full text-left text-[10px] rounded px-1.5 py-1 border border-slate-200 hover:border-violet-300 disabled:opacity-60 truncate"
                  title={[
                    p.owner ? `US ${p.owner}` : "",
                    p.cdDesigner ? `CN ${p.cdDesigner}` : "",
                    p.submissionOwner ? `交图 ${nameOf(p.submissionOwner)}` : "",
                  ].filter(Boolean).join(" · ") || T("未指派")}
                  data-testid={`dv3-mx-mgr-btn-${p.id}`}>
                  {p.owner || p.cdDesigner || p.submissionOwner ? (
                    <span className="flex items-center gap-1.5 min-w-0">
                      {p.owner && <span className="inline-flex items-center gap-0.5 min-w-0"><RoleTag k="US" /><span className="truncate">{p.owner}</span></span>}
                      {p.cdDesigner && <span className="inline-flex items-center gap-0.5 min-w-0"><RoleTag k="CN" /><span className="truncate">{p.cdDesigner}</span></span>}
                      {p.submissionOwner && <span className="inline-flex items-center gap-0.5 min-w-0"><RoleTag k="交图" /><span className="truncate">{nameOf(p.submissionOwner)}</span></span>}
                    </span>
                  ) : <span className="text-slate-400">{T("未指派")}</span>}
                </button>
              </td>
            </tr>
            );
          })}
          {footer && <tr data-testid="dv3-mx-load-more"><td colSpan={99}>{footer}</td></tr>}
        </tbody>
      </ResponsiveTable>
      {edit && createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="dv3-mx-mgr-dialog">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setEdit(null)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-4">
            <p className="text-sm font-bold text-slate-800">{T("项目负责人")}</p>
            <p className="text-[11px] text-slate-400 mt-0.5 truncate">{edit.p.address}</p>
            {/* 2026-10-19 用户调整：去掉「继承 / 完全独立」模式开关与协作负责人多选，
                改为与项目详情「设置」页签**完全同源**的三个字段：
                美国设计师 owner / 中国设计师 cdDesigner / 交图员 submissionOwner。
                前两个走 PATCH 写原字段（详情页也在写同一对），交图员走 managers 接口。 */}
            <div className="mt-3 space-y-2" data-testid="dv3-mx-mgr-fields">
              {([
                ["owner", "美国设计师", "dv3-mx-mgr-owner-select", designerNames],
                ["cdDesigner", "中国设计师", "dv3-mx-mgr-cn-select", designerNames],
              ]).map(([k, label, tid, opts]) => (
                <label key={k} className="flex items-center gap-2" data-testid={`dv3-mx-mgr-field-${k}`}>
                  <span className="text-[11px] font-semibold text-slate-700">{T(label)}</span>
                  <select value={edit[k] || ""} onChange={(e) => setEdit((v) => ({ ...v, [k]: e.target.value }))}
                    className="ml-auto h-7 w-44 rounded border border-slate-200 bg-white px-1.5 text-[11px]"
                    data-testid={tid}>
                    <option value="">{T("未指定")}</option>
                    {/* 当前值可能不在候选里（已离职 / 不在设计部名单），补一个免得被下拉吞掉 */}
                    {edit[k] && !opts.includes(edit[k]) && <option value={edit[k]}>{edit[k]}</option>}
                    {opts.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              ))}
              <label className="flex items-center gap-2" data-testid="dv3-mx-mgr-field-submissionOwner">
                <span className="text-[11px] font-semibold text-slate-700">{T("交图员")}</span>
                <select value={edit.submissionOwner || ""} onChange={(e) => setEdit((v) => ({ ...v, submissionOwner: e.target.value }))}
                  className="ml-auto h-7 w-44 rounded border border-slate-200 bg-white px-1.5 text-[11px]"
                  data-testid="dv3-mx-mgr-submission-owner-select">
                  <option value="">{T("未指定")}</option>
                  {(people || []).map((u) => <option key={u.username} value={u.username}>{u.name || u.username}</option>)}
                  {edit.submissionOwner && !(people || []).some((u) => u.username === edit.submissionOwner) && (
                    <option value={edit.submissionOwner}>{nameOf(edit.submissionOwner)}</option>
                  )}
                </select>
              </label>
            </div>
            <p className="mt-2 text-[10px] leading-4 text-slate-400">
              {T("与项目详情「设置」页签是同一份数据：改这里，详情页的分工与归属同步变化；反向亦然。")}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setEdit(null)} className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs">{T("取消")}</button>
              <button onClick={saveMgr} disabled={busy} data-testid="dv3-mx-mgr-save" className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold disabled:opacity-50">{T("保存")}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const Board = ({ list, meta, onOpen, api, onChanged }) => {
  const [drag, setDrag] = useState(null);
  const [move, setMove] = useState(null);
  const [pick, setPick] = useState(null);
  const [colIdx, setColIdx] = useState(0);
  const railRef = useRef(null);
  const cols = meta.workStatuses.filter((s) => !s.hidden && s.key !== "handed_off" && (s.key !== "pre_sign_concept" || list.some((p) => p.workStatus === "pre_sign_concept")));
  const drop = (to) => {
    if (!drag || drag.workStatus === to) return setDrag(null);
    setMove({ p: drag, to });
    setDrag(null);
  };
  const scrollToCol = (i) => { const el = railRef.current?.children?.[Math.max(0, Math.min(cols.length - 1, i))]; el?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" }); };
  const onScroll = () => { const r = railRef.current; if (!r || !r.children.length) return; const w = r.children[0].getBoundingClientRect().width + 12; setColIdx(Math.max(0, Math.min(cols.length - 1, Math.round(r.scrollLeft / w)))); };
  return (
    <div className="space-y-2" data-testid="dv3-board-wrap">
      <div className="sm:hidden flex items-center gap-2 bg-white rounded-lg border border-slate-200 px-2 py-1.5" data-testid="dv3-board-mobile-nav">
        <button data-testid="dv3-board-prev" onClick={() => scrollToCol(colIdx - 1)} disabled={colIdx === 0} className="p-1 rounded-md text-slate-600 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
        <div className="flex-1 text-center min-w-0"><p className="text-xs font-bold text-slate-800 truncate" data-testid="dv3-board-col-name">{T(cols[colIdx]?.name)}</p><p className="text-[10px] text-slate-400" data-testid="dv3-board-col-idx">{T("{0} / {1} · 左右滑动切换列 · 点卡片「移动」可换状态", [colIdx + 1, cols.length])}</p></div>
        <button data-testid="dv3-board-next" onClick={() => scrollToCol(colIdx + 1)} disabled={colIdx >= cols.length - 1} className="p-1 rounded-md text-slate-600 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div ref={railRef} onScroll={onScroll} className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory sm:snap-none" data-testid="dv3-board">
        {cols.map((s) => {
          const items = list.filter((p) => p.workStatus === s.key);
          return (
            <div key={s.key} data-testid={`dv3-col-${s.key}`} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(s.key)} className={`snap-center min-w-[84vw] w-[84vw] sm:min-w-[230px] sm:w-[230px] shrink-0 rounded-lg border p-2 ${drag ? "border-dashed border-violet-300 bg-violet-50/30" : "border-slate-200 bg-slate-50/60"}`}>
              <div className="flex items-center gap-2 px-1 mb-2"><i className="w-2 h-2 rounded-full" style={{ background: WS_COLOR[s.key]?.c }} /><p className="text-xs font-bold text-slate-800 flex-1 truncate">{T(s.name)}</p><span className="text-[10px] font-bold text-slate-500">{items.length}</span></div>
              <div className="space-y-2 min-h-[60px]">
                {items.map((p) => (
                  /* 行级只读（2026-10-21）：账号查看范围放宽进来的项目（p.readOnlyScope）
                     不参与拖动与「移动到其他状态」，否则拖一下就被后端 403 打回。
                     卡片仍可点击打开详情（只读查看是需求本身）。 */
                  <div key={p.id} draggable={meta.canEdit && !p.readOnlyScope} onDragStart={() => setDrag(p)} onDragEnd={() => setDrag(null)} onClick={() => onOpen(p.id)} data-testid={`dv3-bcard-${p.id}`} data-readonly={p.readOnlyScope ? "1" : "0"}
                    className={`bg-white rounded-lg border p-2.5 cursor-pointer hover:border-violet-400 shadow-sm ${move?.p.id === p.id || pick?.id === p.id ? "border-violet-500 ring-2 ring-violet-200" : "border-slate-200"}`}>
                    <div className="flex items-start gap-1"><p className="text-xs font-semibold text-slate-800 truncate flex-1">{p.address}</p>
                      {meta.canEdit && !p.readOnlyScope && <button data-testid={`dv3-bcard-move-${p.id}`} title={T("移动到其他状态")} onClick={(e) => { e.stopPropagation(); setPick(p); }} className="shrink-0 -mr-1 -mt-1 p-1 rounded-md text-slate-400 hover:text-violet-700 hover:bg-violet-50"><ArrowRightLeft className="h-3.5 w-3.5" /></button>}</div>
                    <p className="text-[10px] text-slate-400 truncate">{p.city} · {p.owner || T("未指派")}</p>
                    <div className="mt-1.5 flex items-center gap-1.5"><Progress v={p.progress} color={WS_COLOR[s.key]?.c} /><span className="text-[10px] font-bold">{formatProgress(p.progress)}</span></div>
                    <div className="mt-1 flex items-center gap-1.5 flex-wrap"><DeadlineBadge p={p} tid={`dv3-bcard-deadline-${p.id}`} /></div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {pick && <MovePicker p={pick} cols={cols} meta={meta} onClose={() => setPick(null)} onPick={(to) => { setPick(null); setMove({ p: pick, to }); }} />}
      {move && <MoveConfirm move={move} meta={meta} api={api} onClose={() => setMove(null)} onDone={() => { setMove(null); onChanged(); }} />}
    </div>
  );
};

const MovePicker = ({ p, cols, meta, onClose, onPick }) => (
  <div className="fixed inset-0 z-[80] bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
    <div className="bg-white rounded-t-2xl sm:rounded-lg w-full sm:max-w-sm p-4 space-y-2 shadow-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="dv3-move-picker">
      <p className="text-sm font-bold text-slate-900">{T("移动到…")}</p>
      <p className="text-xs text-slate-500 truncate" data-testid="dv3-move-picker-address">{T("{0} · 当前", [p.address])} <WsPill ws={p.workStatus} meta={meta} size="sm" /></p>
      <div className="grid gap-1.5">
        {cols.filter((s) => s.key !== p.workStatus).map((s) => (
          <button key={s.key} data-testid={`dv3-move-pick-${s.key}`} onClick={() => onPick(s.key)} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-left text-xs font-semibold text-slate-800 hover:border-violet-400 hover:bg-violet-50"><i className="w-2 h-2 rounded-full" style={{ background: WS_COLOR[s.key]?.c }} />{T(s.name)}<span className="ml-auto text-[10px] text-slate-400 font-normal">{T(s.group)}</span></button>
        ))}
      </div>
      <Button size="sm" variant="outline" className="w-full" data-testid="dv3-move-picker-cancel" onClick={onClose}>{T("取消")}</Button>
    </div>
  </div>
);

const MoveConfirm = ({ move, meta, api, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const from = meta.workStatuses.find((s) => s.key === move.p.workStatus), to = meta.workStatuses.find((s) => s.key === move.to);
  const confirm = async () => {
    if (!meta.isManager && !reason.trim()) return toast.error(T("请填写切换理由，便于主管审批"));
    setBusy(true);
    try { const { data } = await api.post(`/design/v3/projects/${move.p.id}/work-status`, { to: move.to, reason }); toast.success(data.applied ? T("工作状态已更新") : T("已提交审批，待设计主管确认")); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-md p-5 space-y-3 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="dv3-move-dialog">
        <h3 className="font-bold text-slate-900 text-sm">{meta.isManager ? T("确认切换工作状态") : T("申请切换工作状态")}</h3>
        <p className="text-xs text-slate-600 truncate" data-testid="dv3-move-address">{move.p.address}</p>
        <div className="flex items-center gap-2 text-xs"><WsPill ws={from?.key} meta={meta} /><span className="text-slate-400">→</span><WsPill ws={to?.key} meta={meta} tid="dv3-move-to" /></div>
        <label className="block text-xs text-slate-600">{T("理由{0}", [meta.isManager ? T("（可选，写入动态）") : T(" *（写入审批记录）")])}<textarea data-testid="dv3-move-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} autoFocus placeholder={meta.isManager ? T("例如：规划已批复，进入建筑资料包") : T("例如：现场测量已完成，进入平面图设计")} className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm" /></label>
        {!meta.isManager && <p className="text-[11px] text-amber-700 bg-amber-50 rounded-md px-2 py-1" data-testid="dv3-move-notice">{T("非主管切换需设计主管审批（规则 R5），确认后卡片暂不移动，审批通过后生效。")}</p>}
        <div className="flex justify-end gap-2"><Button size="sm" variant="outline" data-testid="dv3-move-cancel" onClick={onClose}>{T("取消")}</Button><Button size="sm" data-testid="dv3-move-confirm" disabled={busy} onClick={confirm} className="bg-slate-900 hover:bg-slate-700">{busy ? T("提交中…") : meta.isManager ? T("确认切换") : T("提交审批")}</Button></div>
      </div>
    </div>
  );
};
